// Renders an engraved DrawnStaff as SVG using the Bravura SMuFL font. Pure
// presentational, in the same hand-rolled idiom as ChordDiagram. All geometry
// comes from engraveStaff in staff-spaces; we scale by spaceSize here. Bravura
// is sized so 1em = 4 staff-spaces (the SMuFL convention).
//
// Multi-staff score: pass `extraStaves` (or the `bassSong` convenience prop)
// and each system renders the authored staves on one shared onset grid, so
// simultaneous notes align and system breaks agree by construction.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import type { Song } from '@/music/song';
import {
  beatFractionAtXInMeasure,
  engraveStaff,
  engraveStaves,
  xAtBeatInMeasure,
  type EngraveOptions,
  type StaffInput,
} from '@/music/notation/engrave';
import type { DrawnMeasure, DrawnStaff, DrawnText } from '@/music/notation/drawn';
import { voicingBounds, type DrawnVoicing } from '@/music/notation/voicingStaff';
import {
  staffSlurs,
  slurVerticalBounds,
  slurTextDodge,
  slurTAtX,
  type SlurCurve,
  type StaffSlurCurve,
} from './slurGeometry';
import { staffTies, tieVerticalBounds, type TieSegment } from '@/music/notation/ties';
import { dynamicBounds } from '@/music/notation/dynamicBounds';
import { staffDynamicPositions } from './scoreDynamicLayout';
import { staffHairpins, hairpinVerticalBounds, type StaffHairpin } from './scoreHairpinLayout';
import { nearestScoreHead, type ScoreEventHitTarget } from './scoreHitTargets';
import { TAB_STRINGS, tabStringY, type TabChord } from '@/music/notation/tab';
import { pitchFromStaffY } from '@/music/notation/editing';
import {
  ENGRAVING,
  SMUFL,
  TUPLET_ENGRAVING,
  glyphChar,
  glyphVerticalBounds,
  ledgerHalfWidth,
  timeSigNumber,
} from '@/music/notation/smufl';
import { chordLabelSpaces, parseChordLabel } from '@/music/notation/chordLabel';
import { onControl, usePassageGesture } from './usePassageGesture';
import {
  SCORE_CLEF_SPACES,
  SCORE_DESKTOP_MARGIN_SPACES,
  SCORE_KEY_SIGNATURE_LEAD,
  scoreTimeSignatureWidth,
} from './scoreLayout';

/** Where on the staff a click landed, for note entry. */
export interface StaffClick {
  barIndex: number;
  /** Authored staff selected for entry (0 = main, 1+ = extra staves). */
  staffIndex: number;
  /** 0..1 across the measure — the caller snaps to a beat grid. */
  beatFraction: number;
  /** Natural MIDI pitch nearest the click. */
  midi: number;
}

/** Temporal insertion position shown while writing. Beats are 1-based; the
 * value `measure beats + 1` denotes the closing barline / next downbeat. */
export interface ScoreEntryCursor {
  barIndex: number;
  beat: number;
}

/** A lane's height in staff-spaces — one number for every system, or a
 *  per-system rule on the system's drawn measures. Returning 0 means "this
 *  system draws nothing in the lane": the space is not reserved and the
 *  render callback is not invoked, so quiet systems close the gap instead
 *  of paying for content they don't have. */
export type SystemLaneSpaces = number | ((measures: DrawnMeasure[], systemIndex: number) => number);

/** Geometry exposed to read-only layers that belong to each engraved system.
 * Coordinates are SVG pixels in the parent ScoreView. */
export interface ScoreSystemOverlayContext {
  systemIndex: number;
  measures: DrawnMeasure[];
  top: number;
  height: number;
  spaceSize: number;
  xAtBeat: (barIndex: number, beat: number) => number | null;
  left: number;
  right: number;
}

/** A curly brace spanning y1..y2 with its spine at x, bulging left — the
 *  keyboard-pair mark, hand-drawn so it scales with the staff. */
function braceD(x: number, y1: number, y2: number, s: number): string {
  const mid = (y1 + y2) / 2;
  const w = 0.95 * s;
  return [
    `M ${x} ${y1}`,
    `C ${x - w} ${y1 + (mid - y1) * 0.3}, ${x - w * 0.15} ${mid - (mid - y1) * 0.25}, ${x - w} ${mid}`,
    `C ${x - w * 0.15} ${mid + (y2 - mid) * 0.25}, ${x - w} ${y2 - (y2 - mid) * 0.3}, ${x} ${y2}`,
  ].join(' ');
}

const FONT_URL = `${import.meta.env.BASE_URL}fonts/Bravura.woff2`;
const BRAVURA_FACE = `@font-face{font-family:'Bravura';src:url('${FONT_URL}') format('woff2');font-display:block;}`;

// Horizontal room (in staff-spaces) reserved at the start of each system for
// the clef and, on the first system, the time signature. The music begins
// after this header so the two never collide.
const CLEF_SPACES = SCORE_CLEF_SPACES;
// Text (lyrics, cues, labels) defaults to an Arial-metric stack so the
// engraver's char-width constants hold across platforms. Paper opts into its
// serif reading face; notation glyphs and structural numbers stay explicit.
const TEXT_FONT =
  "var(--score-annotation-font, 'Helvetica Neue', Helvetica, Arial, 'Liberation Sans', sans-serif)";

/** Clefs occupy the shared header column on every system, even an empty one. */
function clefExtentsOf(drawn: Pick<DrawnStaff, 'clef' | 'clefY' | 'staffLines'>) {
  const bounds = glyphVerticalBounds(drawn.clef);
  return {
    rise: Math.max(0, -(drawn.clefY + bounds.top)),
    drop: Math.max(0, drawn.clefY + bounds.bottom - (drawn.staffLines - 1)),
  };
}

export interface ScoreViewProps {
  song: Song;
  options?: EngraveOptions;
  className?: string;
  ariaLabel?: string;
  /** Highlight this melody note. */
  selectedId?: string | null;
  selectedHeadIndex?: number | null;
  /** Click on empty staff to place a note. */
  onStaffClick?: (click: StaffClick) => void;
  /** Click on an existing note. */
  onSelectNote?: (id: string, event?: { shiftKey: boolean; headIndex?: number }) => void;
  /** Studio Select mode: enlarge touch taps around heads/rests without changing
   * engraving or desktop clicks. Overlaps resolve to the nearest event. */
  touchNoteTargets?: boolean;
  /** The one authored staff that accepts note entry/selection and owns the
   * entry caret. Defaults to the main staff. Other staves remain read-only. */
  interactiveStaffIndex?: number;
  /** Highlight a written bar by its Song bar id. Score projections preserve
   * the canonical Score.form measure id here. */
  selectedMeasureId?: string | null;
  /** Extra bars in a passage range (practice loop / copy). */
  selectedMeasureIds?: ReadonlySet<string>;
  /** Select a written bar independently of note-entry mode. */
  onSelectMeasure?: (id: string) => void;
  /** Drag (a finger holds first) or shift-extend a written-bar range. */
  onSelectPassage?: (fromId: string, toId: string, opts?: { extend?: boolean }) => void;
  /** Fired when a drag or hold committed a selection. */
  onPassageGesture?: () => void;
  /** Plain click dismisses the loop range (DAW click-out). */
  onClearPassage?: () => void;
  /** A reading surface: a tap on a bar is the start cursor (and takes the
   * band there). In place of onClearPassage; never with note entry. */
  onTapMeasure?: (id: string) => void;
  /** The written bar Play starts from, marked at its start. */
  cursorMeasureId?: string | null;
  /** Highlight one authored harmony symbol by its canonical id. */
  selectedChordId?: string | null;
  /** Select an authored harmony symbol independently of note-entry mode. */
  onSelectChord?: (id: string) => void;
  /** Enlarge chord taps into available space, clear of neighbouring symbols and staves. */
  touchChordTargets?: boolean;
  /** What activating a chord symbol DOES, for the accessible name: the studio
   *  edits ("Edit chord C7"), the workbench notation pane plays. */
  chordActionVerb?: string;
  /** Double-click/double-tap on a chord symbol (the workbench opens the
   *  voicing inspector, mirroring the diagram cards' convention). */
  onChordDoubleClick?: (id: string) => void;
  /** Visible insertion caret for sequential note entry. */
  entryCursor?: ScoreEntryCursor | null;
  /** Grand staff: a second song rendered on a bass-clef staff inside each
   *  system. Shares the bar grid and the engrave options.
   *  Sugar for extraStaves=[{song, clef: 'bass'}]. */
  bassSong?: Song;
  /** N further staves stacked under the main one, jointly engraved so every
   *  bar shares one onset grid and identical
   *  widths — system k of every staff is bar-for-bar aligned. Supersedes
   *  bassSong when both are given. */
  extraStaves?: StaffInput[];
  /** Per-staff presentation (index 0 = the main staff, then extraStaves):
   *  `label` prints in the left margin of the FIRST system; consecutive
   *  staves sharing a `group` get a curly brace (a piano's two staves), and
   *  when the stack spans more than one group a square bracket joins the
   *  whole system (the ensemble score convention). */
  staffMeta?: { label?: string; group?: string }[];
  /** Voice-led chords to engrave on a lower (treble-8vb) staff. */
  voicings?: DrawnVoicing[];
  /** Guitar tab for the lower staff; takes precedence over `voicings` when set. */
  tab?: TabChord[];
  /** Absolute beat position of a playback cursor, or null/undefined for none. */
  playheadBeat?: number | null;
  /** Page view: render only systems [start, end). The whole song is engraved
   * as ever (system breaks identical on every page); the strip is CUT at each
   * system's above-furniture top and the svg's viewBox windows that slice, so
   * no coordinate moves — anything a sibling layer draws outside the window
   * simply clips. */
  systemWindow?: readonly [number, number];
  /** A small bar number above the start of every bar (default on). An
   * anacrusis is not counted: the first FULL bar is 1, as every engraver
   * numbers it. */
  barNumbers?: boolean;
  /** Page view: reports each system's slice height (px, cut-to-cut) whenever
   * the engraving changes, so a pager can pack systems into pages without
   * duplicating the stride maths. */
  onSystemSpans?: (spans: number[]) => void;
  /** Vertical lane reserved above each system for a read-only layer. */
  systemOverlayHeight?: SystemLaneSpaces;
  /** Render a read-only layer above each system, using exact engraving x positions. */
  renderSystemOverlay?: (context: ScoreSystemOverlayContext) => ReactNode;
  /** Vertical lane reserved BELOW each system (staff-spaces) — the analysis
   *  lane's home, mirroring the overlay above. */
  systemUnderlayHeight?: SystemLaneSpaces;
  /** Render a read-only layer below each system (same context contract). */
  renderSystemUnderlay?: (context: ScoreSystemOverlayContext) => ReactNode;
  /** Blank paper at each horizontal edge, in staff-spaces. */
  horizontalMarginSpaces?: number;
  /** Optional trailing margin when an embedded score uses asymmetric gutters. */
  horizontalEndMarginSpaces?: number;
}

export function ScoreView({
  song,
  options,
  className,
  ariaLabel,
  selectedId,
  selectedHeadIndex,
  onStaffClick,
  onSelectNote,
  touchNoteTargets = false,
  interactiveStaffIndex = 0,
  selectedMeasureId,
  selectedMeasureIds,
  onSelectMeasure,
  onSelectPassage,
  onPassageGesture,
  onClearPassage,
  onTapMeasure,
  cursorMeasureId,
  selectedChordId,
  onSelectChord,
  touchChordTargets = false,
  chordActionVerb = 'Edit',
  onChordDoubleClick,
  entryCursor,
  bassSong,
  extraStaves,
  staffMeta,
  voicings,
  tab,
  playheadBeat,
  systemWindow,
  barNumbers = true,
  onSystemSpans,
  systemOverlayHeight = 0,
  renderSystemOverlay,
  systemUnderlayHeight = 0,
  renderSystemUnderlay,
  horizontalMarginSpaces = SCORE_DESKTOP_MARGIN_SPACES,
  horizontalEndMarginSpaces,
}: ScoreViewProps) {
  // Grand staff / full score: engrave every staff JOINTLY so all bars share
  // one onset-column grid — simultaneous notes align across the staves.
  const extras = useMemo(
    (): StaffInput[] =>
      extraStaves ?? (bassSong ? [{ song: bassSong, clef: 'bass' as const }] : []),
    [extraStaves, bassSong],
  );
  // A two-digit meter (12/8) needs a wider header than the single-digit default.
  const timeSigW = scoreTimeSignatureWidth(song.timeSignature);
  const { staff, extraDrawn } = useMemo((): { staff: DrawnStaff; extraDrawn: DrawnStaff[] } => {
    // System 1's header also carries the time signature; telling the engraver
    // lets justification land every system's right margin on the same line.
    // The app's own staves read guitar-written treble-8vb unless told otherwise.
    const opts: EngraveOptions = {
      clef: 'treble8vb',
      firstSystemIndent: timeSigW,
      ...options,
      harmonyExtents: [
        ...(options?.harmonyExtents ?? []),
        ...(!tab && voicings
          ? voicings.map((v) => ({ barIndex: v.barIndex, beat: v.beat, ...voicingBounds(v) }))
          : []),
      ],
    };
    if (extras.length > 0) {
      const [main, ...rest] = engraveStaves([{ song, clef: opts.clef }, ...extras], opts);
      return { staff: main, extraDrawn: rest };
    }
    return { staff: engraveStaff(song, opts), extraDrawn: [] };
  }, [song, extras, options, timeSigW, tab, voicings]);
  const localMainSlurs = useMemo(() => staffSlurs(staff, song.slurs), [staff, song.slurs]);
  const localExtraSlurs = useMemo(
    () => extraDrawn.map((d, k) => staffSlurs(d, extras[k]?.song.slurs)),
    [extraDrawn, extras],
  );
  const mainTies = useMemo(() => staffTies(staff), [staff]);
  const extraTies = useMemo(() => extraDrawn.map(staffTies), [extraDrawn]);
  const dynamicPositions = useMemo(() => {
    const positions = staffDynamicPositions(staff, localMainSlurs);
    extraDrawn.forEach((d, k) => {
      for (const [mark, y] of staffDynamicPositions(d, localExtraSlurs[k])) positions.set(mark, y);
    });
    return positions;
  }, [staff, extraDrawn, localMainSlurs, localExtraSlurs]);
  const mainHairpins = useMemo(
    () => staffHairpins(staff, song.hairpins, localMainSlurs, dynamicPositions),
    [staff, song.hairpins, localMainSlurs, dynamicPositions],
  );
  const extraHairpins = useMemo(
    () =>
      extraDrawn.map((d, k) =>
        staffHairpins(d, extras[k]?.song.hairpins, localExtraSlurs[k], dynamicPositions),
      ),
    [extraDrawn, extras, localExtraSlurs, dynamicPositions],
  );
  // Dodge staff text in local coordinates before reserving row and lane space.
  const textDodge = useMemo(() => {
    const dodge = new Map<DrawnText, number>();
    const collectTextDodges = (
      drawn: DrawnStaff,
      curves: StaffSlurCurve[],
      hairpins: StaffHairpin[],
    ) => {
      drawn.systems.forEach((sys, si) => {
        for (const m of sys.measures)
          for (const t of m.texts) {
            const x0 = m.x + t.x;
            let yc = t.y;
            for (const sc of curves) {
              if (sc.si !== si) continue;
              const ny = slurTextDodge(
                sc.c,
                { x0, x1: x0 + 0.84 * t.text.length, yc, halfH: 0.95 },
                t.y < 2,
                1,
              );
              if (ny != null) yc = ny;
            }
            // Resolve below cues outward through all annotation ink, including
            // curves again if moving past a dynamic entered a lower slur.
            if (t.y >= 2) {
              const right = x0 + 0.84 * t.text.length;
              const bands: { top: number; bottom: number }[] = [];
              for (const other of sys.measures)
                for (const d of other.dynamics) {
                  const b = dynamicBounds(d),
                    y = dynamicPositions.get(d) ?? d.y;
                  if (other.x + d.x + b.right < x0 || other.x + d.x + b.left > right) continue;
                  bands.push({ top: y + b.top, bottom: y + b.bottom });
                }
              for (const h of hairpins)
                if (h.si === si && h.x2 >= x0 && h.x1 <= right)
                  bands.push(hairpinVerticalBounds(h));
              for (const sc of curves) {
                if (sc.si !== si) continue;
                const left = Math.max(x0 - 0.2, sc.c.ax1),
                  end = Math.min(right + 0.2, sc.c.ax2);
                if (end < left) continue;
                bands.push(slurVerticalBounds(sc.c, slurTAtX(sc.c, left), slurTAtX(sc.c, end)));
              }
              bands.sort((a, b) => a.bottom - b.bottom);
              for (const b of bands)
                if (yc + 0.95 >= b.top - 0.4 && yc - 0.95 <= b.bottom + 0.4)
                  yc = Math.max(yc, b.bottom + 0.95 + 0.4);
            }
            if (yc !== t.y) dodge.set(t, yc);
          }
      });
    };
    collectTextDodges(staff, localMainSlurs, mainHairpins);
    extraDrawn.forEach((drawn, k) =>
      collectTextDodges(drawn, localExtraSlurs[k], extraHairpins[k]),
    );
    return dodge;
  }, [
    staff,
    extraDrawn,
    localMainSlurs,
    localExtraSlurs,
    dynamicPositions,
    mainHairpins,
    extraHairpins,
  ]);
  const s = staff.spaceSize;
  const emSize = 4 * s; // Bravura: 1em = 4 staff-spaces
  const KEYSIG_STEP = 1.0; // horizontal spacing per key-signature accidental
  const KEYSIG_LEAD = SCORE_KEY_SIGNATURE_LEAD; // gap between the clef and the first accidental
  // Every staff's music starts at one x, so the header reserves the WIDEST
  // signature in the stack (a full score's staves read in different written
  // keys); a staff with fewer accidentals leaves the space after its own.
  const keySigCount = Math.max(...[staff, ...extraDrawn].map((st) => st.keySignature.length));
  const keySigW = keySigCount ? KEYSIG_LEAD + keySigCount * KEYSIG_STEP : 0;

  const staffHeight = (staff.staffLines - 1) * s;

  // Staff labels + grouping (full score / grand staff). Labels widen the
  // left margin; brackets and braces live inside the base margin, left of
  // the system line.
  const staffCount = 1 + extraDrawn.length;
  // Invalid/stale targets fail closed instead of silently redirecting an edit
  // to staff 0 while a different staff is visible in the editor controls.
  const interactiveIndex =
    Number.isInteger(interactiveStaffIndex) &&
    interactiveStaffIndex >= 0 &&
    interactiveStaffIndex < staffCount
      ? interactiveStaffIndex
      : null;
  const meta = staffMeta ?? [];
  const labels = Array.from({ length: staffCount }, (_, k) => meta[k]?.label);
  const hasLabels = labels.some(Boolean);
  const LABEL_FONT = 1.1; // staff-spaces
  const labelW = hasLabels
    ? (Math.min(16, Math.max(...labels.filter(Boolean).map((l) => l!.length))) * 0.52 + 1.2) *
      LABEL_FONT *
      s
    : 0;
  // Consecutive staves sharing a group -> one brace per multi-staff run.
  const braceRuns: { from: number; to: number }[] = [];
  for (let k = 0; k < staffCount; k++) {
    const g = meta[k]?.group;
    if (!g) continue;
    const last = braceRuns[braceRuns.length - 1];
    if (last && meta[k - 1]?.group === g && last.to === k - 1) last.to = k;
    else braceRuns.push({ from: k, to: k });
  }
  const braces = braceRuns.filter((r) => r.to > r.from);
  const distinctGroups = new Set(
    Array.from({ length: staffCount }, (_, k) => meta[k]?.group).filter(Boolean),
  );
  const hasBracket = staffCount > 1 && distinctGroups.size > 1;

  const marginX = horizontalMarginSpaces * s + labelW;
  const endMarginX = horizontalEndMarginSpaces == null ? marginX : horizontalEndMarginSpaces * s;

  // Chord symbols share one row per system, floated above the tallest thing in
  // that system (stem tips, beams, ledger lines) so upward beams never collide
  // with them. y grows downward, so "tallest" is the most-negative yBelowTop.
  // A first bar shorter than the meter is an anacrusis: unnumbered, so the
  // first full bar reads 1 (the engraving convention; a padded-to-full pickup
  // bar, the openbook import's, simply counts as bar 1).
  const firstDrawn = staff.systems[0]?.measures[0];
  const anacrusis = !!firstDrawn && firstDrawn.beats < (song.timeSignature?.[0] ?? 4) - 1e-6;
  const chordBaseline = staff.systems.map((sys, si) => {
    let top = 0;
    for (const m of sys.measures) {
      for (const nte of m.notes) {
        top = Math.min(top, nte.y, ...(nte.stem ? [nte.stem.y1, nte.stem.y2] : []), ...nte.ledger);
        // A fermata over a closing note sits above its stem tip, where the
        // chord symbol for that beat used to be drawn through it.
        for (const ornament of nte.ornaments ?? []) top = Math.min(top, ornament.y - 0.6);
      }
      for (const b of m.beams) top = Math.min(top, b.y1, b.y2);
      for (const rest of m.rests) {
        top = Math.min(
          top,
          rest.y + glyphVerticalBounds(rest.glyph).top * (rest.cue ? ENGRAVING.cueScale : 1),
        );
        for (const ornament of rest.ornaments ?? []) top = Math.min(top, ornament.y - 0.6);
      }
      // Tuplet figures/brackets above the staff occupy the same band — an
      // unbeamed quarter-triplet bracket used to strike through "Dm/C".
      for (const t of m.tuplets)
        if (!t.below) top = Math.min(top, t.y - TUPLET_ENGRAVING.halfHeight);
      // Above cues can move above a tuplet during collision clearance. Their
      // centred text is 1.68 spaces tall; keep the chord row above that ink.
      for (const text of m.texts)
        if ((textDodge.get(text) ?? text.y) < 0)
          top = Math.min(top, (textDodge.get(text) ?? text.y) - 0.84);
    }
    for (const curve of localMainSlurs.filter((c) => c.si === si))
      top = Math.min(top, slurVerticalBounds(curve.c).top);
    for (const tie of mainTies.filter((t) => t.si === si))
      top = Math.min(top, tieVerticalBounds(tie).top);
    return Math.min(-1.2, top - 1.2); // ≥1.2 spaces above the top line and above any glyph
  });
  // MARKS LANE above the chord row (the lead-sheet reference layout):
  // boxed rehearsal/section letters at their section's first bar, and volta
  // brackets. Sections come from the song's section names ('Main' is the
  // synthesized no-section name — never drawn); a volta bracket starts at an
  // ending-marked bar and runs to the bar that closes with a repeat/final/
  // double barline, or until the next ending/section takes over.
  const sectionStarts = useMemo(() => {
    const map = new Map<number, string>();
    let barIndex = 0;
    for (const sec of song.sections) {
      if (sec.name && sec.name !== 'Main') map.set(barIndex, sec.name);
      barIndex += sec.bars.length;
    }
    return map;
  }, [song.sections]);
  const voltaSpans = useMemo(() => {
    const all = staff.systems.flatMap((sys) => sys.measures);
    const spans: { from: number; to: number; n: number; closed: boolean }[] = [];
    all.forEach((m, i) => {
      if (m.ending == null) return;
      let j = i;
      while (j < all.length) {
        const mm = all[j];
        if (mm.barline === 'repeat' || mm.barline === 'final' || mm.barline === 'double') break;
        const nxt = all[j + 1];
        if (!nxt || nxt.ending != null || sectionStarts.has(nxt.barIndex)) break;
        j++;
      }
      spans.push({
        from: m.barIndex,
        to: all[j].barIndex,
        n: m.ending,
        // A volta ending on a backward repeat closes its bracket; the last
        // alternative stays open-ended (the engraving convention).
        closed: all[j].barline === 'repeat',
      });
    });
    return spans;
  }, [staff, sectionStarts]);
  // Above-staff furniture stacks in LANES, bottom-up: chord symbols, then
  // section letters / coda·segno glyphs / directives, then volta brackets on
  // top — the engraving convention, and the only way a ⊕ on a chord-carrying
  // bar (Body And Soul's coda) or a volta over a chorded ending stays
  // readable. A lane claims height only on systems that use it.
  const navInSys = staff.systems.map((sys) =>
    sys.measures.some(
      (m) => m.directive || m.feel || m.segno || m.coda || sectionStarts.has(m.barIndex),
    ),
  );
  const voltaInSys = staff.systems.map((sys) => {
    const first = sys.measures[0]?.barIndex ?? 0;
    const last = sys.measures[sys.measures.length - 1]?.barIndex ?? -1;
    return voltaSpans.some((sp) => sp.to >= first && sp.from <= last);
  });
  const hasMarks = navInSys.some(Boolean) || voltaInSys.some(Boolean);
  /** Centre of a system's marks band: clear of the tallest chord ascenders. */
  const marksC = (si: number) => chordBaseline[si] - 3.3;
  /** A system's volta line rides above its marks band when one exists. */
  const voltaY = (si: number) => chordBaseline[si] - (navInSys[si] ? 6.6 : 4.2);

  // PER-SYSTEM headroom: each system reserves the TOP EXTENT of its own
  // lane stack beyond its chord baseline (the volta line sits 6.6 above it
  // when marks share the system). A global max floated every markless
  // system's furniture a volta-band too high; under-reserving rode the
  // bracket + ⊕ into the staff above and the comping view's diagram row.
  const marksTopOf = (si: number) =>
    voltaInSys[si] ? (navInSys[si] ? 6.8 : 4.4) : navInSys[si] ? 4.6 : 0;
  const aboveNeeds = staff.systems.map(
    (_, si) => Math.max(1.2, -(chordBaseline[si] ?? -1.2)) + marksTopOf(si),
  );
  // The tempo row needs its font height as well as breathing room; reserving
  // only the gap lets the first chord's ascenders meet the tempo baseline.
  const tempoTextSize = Math.max(13, 2.2 * s);
  const tempoTextPad = song.tempoText ? 2.6 * s + tempoTextSize : 0;
  // Lane space is PER SYSTEM: a function spec lets the layer decline systems
  // it would leave empty (an intro with no changes reserves no grip lane).
  const laneHeights = (spec: SystemLaneSpaces, enabled: boolean): number[] =>
    staff.systems.map((sys, si) =>
      enabled ? Math.max(0, typeof spec === 'function' ? spec(sys.measures, si) : spec) * s : 0,
    );
  const overlayHeights = laneHeights(systemOverlayHeight, !!renderSystemOverlay);
  const underlayHeights = laneHeights(systemUnderlayHeight, !!renderSystemUnderlay);
  const marginTop = ((aboveNeeds[0] ?? 1.2) + 2.0) * s + tempoTextPad + (overlayHeights[0] ?? 0);

  // Every extra staff sits under the one above it with room for whatever
  // rides above/below its lines — measured PER SYSTEM, so a quiet system
  // tightens up and a busy one opens out instead of every system paying the
  // whole score's worst case. sysStack[si].tops[k] is the px offset of extra
  // staff k's TOP line relative to that system's y0; .block is the height
  // the stack adds below the main staff in that system.
  const hasExtras =
    extraDrawn.length > 0 && extraDrawn.every((d) => d.systems.length === staff.systems.length);
  // Sung verse lanes below the MAIN staff (imported vocal lines): per system,
  // how many verses sing and where the first lane sits — below every drawn
  // element (low stems, dynamics, below-cues), never inside the staff.
  const lyricLanes = useMemo(
    () =>
      staff.systems.map((sys, si) => {
        let verses = 0;
        let deepest = staff.staffLines - 1;
        for (const m of sys.measures) {
          for (const l of m.lyrics) verses = Math.max(verses, l.verse + 1);
          for (const nte of m.notes) {
            deepest = Math.max(deepest, nte.y, ...(nte.stem ? [nte.stem.y1, nte.stem.y2] : []));
          }
          for (const d of m.dynamics)
            deepest = Math.max(deepest, (dynamicPositions.get(d) ?? d.y) + dynamicBounds(d).bottom);
          for (const t of m.texts) deepest = Math.max(deepest, (textDodge.get(t) ?? t.y) + 0.95);
          for (const r of m.rests)
            deepest = Math.max(
              deepest,
              r.y + glyphVerticalBounds(r.glyph).bottom * (r.cue ? ENGRAVING.cueScale : 1),
            );
          for (const t of m.tuplets) deepest = Math.max(deepest, t.y + TUPLET_ENGRAVING.halfHeight);
        }
        for (const curve of localMainSlurs.filter((c) => c.si === si))
          deepest = Math.max(deepest, slurVerticalBounds(curve.c).bottom);
        for (const tie of mainTies.filter((t) => t.si === si))
          deepest = Math.max(deepest, tieVerticalBounds(tie).bottom);
        for (const h of mainHairpins.filter((h) => h.si === si))
          deepest = Math.max(deepest, hairpinVerticalBounds(h).bottom);
        return { verses, base: Math.max(7.0, deepest + 1.7) };
      }),
    [staff, textDodge, localMainSlurs, mainTies, dynamicPositions, mainHairpins],
  );
  // Depth (spaces below the BOTTOM line) the lanes claim in each system.
  const lyricDrop = useCallback(
    (si: number) => {
      const lane = lyricLanes[si];
      return lane.verses > 0 ? lane.base + lane.verses * 1.7 - (staff.staffLines - 1) : 0;
    },
    [lyricLanes, staff.staffLines],
  );
  const sysStack = useMemo(() => {
    // Per-BAR extremes: the previous staff's below-staff content and the next
    // staff's above-staff content may share the gap ONLY when they happen in
    // different bars — at the same bar they collide, so that bar reserves the
    // full sum (the Telemann's "(a 2)" system drove a bass beam into the
    // treble's low stems under the damped-sharing rule).
    const bottomLine = staff.staffLines - 1;
    const extremesOf = (
      measures: DrawnMeasure[],
      si: number,
      curves: StaffSlurCurve[],
      ties: TieSegment[],
      hairpins: StaffHairpin[],
    ) => {
      const perBar = new Map<number, { rise: number; drop: number }>();
      let rise = 0;
      let drop = 0;
      const see = (barIndex: number, y: number) => {
        const e = perBar.get(barIndex) ?? { rise: 0, drop: 0 };
        e.rise = Math.max(e.rise, -y);
        e.drop = Math.max(e.drop, y - bottomLine);
        perBar.set(barIndex, e);
        rise = Math.max(rise, -y);
        drop = Math.max(drop, y - bottomLine);
      };
      for (const m of measures) {
        for (const nte of m.notes) {
          for (const y of [nte.y, ...(nte.stem ? [nte.stem.y1, nte.stem.y2] : []), ...nte.ledger])
            see(m.barIndex, y);
        }
        for (const r of m.rests) {
          const bounds = glyphVerticalBounds(r.glyph);
          const scale = r.cue ? ENGRAVING.cueScale : 1;
          see(m.barIndex, r.y + bounds.top * scale);
          see(m.barIndex, r.y + bounds.bottom * scale);
          for (const ornament of r.ornaments ?? []) see(m.barIndex, ornament.y - 0.6);
        }
        for (const t of m.texts) {
          const y = textDodge.get(t) ?? t.y;
          see(m.barIndex, y - 0.95);
          see(m.barIndex, y + 0.95);
        }
        for (const d of m.dynamics) {
          const bounds = dynamicBounds(d),
            y = dynamicPositions.get(d) ?? d.y;
          see(m.barIndex, y + bounds.top);
          see(m.barIndex, y + bounds.bottom);
        }
        // Tuplet figures ride above the group — without them a "6" above the
        // piano staff sat exactly where the voice's third verse lane landed.
        for (const t of m.tuplets) {
          see(m.barIndex, t.y - TUPLET_ENGRAVING.halfHeight);
          see(m.barIndex, t.y + TUPLET_ENGRAVING.halfHeight);
        }
      }
      for (const shape of [
        ...hairpins
          .filter((h) => h.si === si)
          .map((h) => ({ x1: h.x1, x2: h.x2, ...hairpinVerticalBounds(h) })),
        ...curves
          .filter((c) => c.si === si)
          .map((c) => ({ x1: c.c.ax1, x2: c.c.ax2, ...slurVerticalBounds(c.c) })),
        ...ties
          .filter((t) => t.si === si)
          .map((t) => ({ x1: t.x1, x2: t.x2, ...tieVerticalBounds(t) })),
      ])
        for (const m of measures) {
          if (shape.x2 < m.x || shape.x1 > m.x + m.width) continue;
          see(m.barIndex, shape.top);
          see(m.barIndex, shape.bottom);
        }
      return { rise, drop, perBar };
    };
    return staff.systems.map((sys, si) => {
      if (!hasExtras) return { tops: [] as number[], block: lyricDrop(si) * s };
      let prev = extremesOf(sys.measures, si, localMainSlurs, mainTies, mainHairpins);
      let prevClef = clefExtentsOf(staff);
      let prevLyric = lyricDrop(si);
      let acc = staffHeight;
      const tops = extraDrawn.map((drawnStaff, k) => {
        const next = extremesOf(
          drawnStaff.systems[si]?.measures ?? [],
          si,
          localExtraSlurs[k],
          extraTies[k],
          extraHairpins[k],
        );
        const nextClef = clefExtentsOf(drawnStaff);
        // Same-bar drop+rise pairs need their full sum; different bars share.
        let pairNeed = 0;
        for (const [bi, pe] of prev.perBar) {
          const ne = next.perBar.get(bi);
          if (ne) pairNeed = Math.max(pairNeed, pe.drop + ne.rise);
        }
        // Lyric lanes run the system's FULL width, so they can't share the
        // gap with the next staff's above-staff content in any bar — the
        // lane depth and the rise SUM (max'd them, and the Ave Maria's third
        // verse printed straight over the piano's tuplet figures).
        const lyricNeed = prevLyric > 0 ? prevLyric + next.rise : 0;
        // Clefs share the header's x position, so their ink needs its full
        // drop + rise, independently of the music's per-bar extremes.
        const clefNeed = prevClef.drop + nextClef.rise;
        const gap = Math.max(prev.drop * 0.6 + next.rise * 0.6, pairNeed, lyricNeed, clefNeed);
        const top = acc + (1.4 + Math.max(0, gap)) * s;
        acc = top + staffHeight + Math.max(0.6, next.drop * 0.6) * s;
        prev = next;
        prevClef = nextClef;
        prevLyric = 0;
        return top;
      });
      // Reserve the last clef's descender below the stack; intermediate
      // clefs already receive clearance in the following staff's gap.
      return {
        tops,
        block: Math.max(acc - staffHeight, tops[tops.length - 1] + prevClef.drop * s),
      };
    });
  }, [
    staff,
    extraDrawn,
    hasExtras,
    lyricDrop,
    s,
    staffHeight,
    textDodge,
    localMainSlurs,
    localExtraSlurs,
    mainTies,
    extraTies,
    dynamicPositions,
    mainHairpins,
    extraHairpins,
  ]);

  // Optional lower staff below the melody (and below the bass staff when the
  // grand staff is present): guitar tab (6 lines) or the voiced chords in
  // notation (treble 8vb). Tab takes precedence when supplied.
  const hasTab = !!tab && tab.length > 0;
  const hasVoicing = !hasTab && !!voicings && voicings.length > 0;
  const hasLower = hasTab || hasVoicing;
  // Tab lines sit wider apart than staff lines so two-digit frets ("10")
  // fit between them instead of colliding with their neighbours.
  const TAB_LINE_SPACING = 1.5;
  const lowerLines = hasTab ? TAB_STRINGS : staff.staffLines;
  const lowerHeight = (lowerLines - 1) * (hasTab ? TAB_LINE_SPACING : 1) * s;
  // Space between the staff above and the lower staff, plus room below it:
  // voicing stacks ride ledger lines well above (and below) their staff, so
  // the gaps grow with the actual extremes instead of clipping upward.
  const voicingYs = hasVoicing ? voicings!.flatMap((v) => v.notes.map((n) => n.y)) : [];
  const voicingRise = Math.max(0, ...voicingYs.map((y) => -y)); // spaces above the top line
  const voicingDrop = Math.max(0, ...voicingYs.map((y) => y - (lowerLines - 1)));
  const lowerClef = hasVoicing
    ? clefExtentsOf({ clef: SMUFL.gClef8vb, clefY: 3, staffLines: staff.staffLines })
    : { rise: 0, drop: 0 };
  const lastClef = clefExtentsOf(extraDrawn[extraDrawn.length - 1] ?? staff);
  const voicingGap =
    (hasVoicing
      ? Math.max(3 + voicingRise, (hasExtras ? 0 : lastClef.drop) + lowerClef.rise + 1.4)
      : 3 + voicingRise) * s;
  const voicingBlock = hasLower
    ? voicingGap + lowerHeight + Math.max(voicingDrop, lowerClef.drop) * s
    : 0;
  // How far each system's MAIN staff content descends below its bottom line
  // (spaces). sysStack's block covers extra staves and lyric lanes; a bare
  // lead sheet's low ledger notes were covered by NOTHING, and the next
  // system's diagram overlay drew straight through them.
  const mainDrop = staff.systems.map((sys, si) => {
    const bottom = staff.staffLines - 1;
    let drop = 0;
    for (const m of sys.measures) {
      for (const nte of m.notes) {
        for (const y of [nte.y, ...(nte.stem ? [nte.stem.y1, nte.stem.y2] : []), ...nte.ledger])
          drop = Math.max(drop, y - bottom);
      }
      for (const r of m.rests)
        drop = Math.max(
          drop,
          r.y + glyphVerticalBounds(r.glyph).bottom * (r.cue ? ENGRAVING.cueScale : 1) - bottom,
        );
      for (const t of m.texts) drop = Math.max(drop, (textDodge.get(t) ?? t.y) + 0.95 - bottom);
      for (const d of m.dynamics)
        drop = Math.max(drop, (dynamicPositions.get(d) ?? d.y) + dynamicBounds(d).bottom - bottom);
      for (const t of m.tuplets) drop = Math.max(drop, t.y + TUPLET_ENGRAVING.halfHeight - bottom);
    }
    for (const curve of localMainSlurs.filter((c) => c.si === si))
      drop = Math.max(drop, slurVerticalBounds(curve.c).bottom - bottom);
    for (const tie of mainTies.filter((t) => t.si === si))
      drop = Math.max(drop, tieVerticalBounds(tie).bottom - bottom);
    for (const h of mainHairpins.filter((h) => h.si === si))
      drop = Math.max(drop, hairpinVerticalBounds(h).bottom - bottom);
    return drop;
  });
  // The gap after a system clears ITS below-staff content (the deeper of the
  // sub-staff stack and the main staff's own descenders) plus the NEXT
  // system's above-staff needs — per system, so quiet pages tighten and a
  // marked system opens exactly as much as its furniture demands.
  const strides = sysStack.map((st, si) => {
    const below = Math.max(st.block + voicingBlock, mainDrop[si] * s);
    const nextAbove = si + 1 < staff.systems.length ? aboveNeeds[si + 1] : 0;
    return (
      staffHeight +
      below +
      (nextAbove + 3.0) * s +
      (overlayHeights[si + 1] ?? 0) +
      underlayHeights[si]
    );
  });
  const yTops = strides.map((_, si) => marginTop + strides.slice(0, si).reduce((a, b) => a + b, 0));

  // Absolute start beat of each bar (by global bar index) for the playhead.
  const barStartByIndex = useMemo(() => {
    const map = new Map<number, number>();
    const all = staff.systems
      .flatMap((sys) => sys.measures)
      .sort((a, b) => a.barIndex - b.barIndex);
    let acc = 0;
    for (const m of all) {
      map.set(m.barIndex, acc);
      acc += m.beats;
    }
    return map;
  }, [staff]);

  // DrawnMeasure carries the stable global bar index used by the engraver;
  // the Song retains the domain id that Score.form and the chart editor use.
  // Keeping this mapping at the presentation seam lets notation select the
  // same canonical measure without teaching the engraver about editor state.
  const measureIdByBarIndex = useMemo(
    () => new Map(song.sections.flatMap((section) => section.bars).map((bar, i) => [i, bar.id])),
    [song.sections],
  );

  const contentWidth = Math.max(...staff.systems.map((sys) => sys.width), 20);
  // firstSystemIndent already trades two content spaces for the time signature,
  // so adding them again here only creates blank paper on the right edge.
  const width = marginX + endMarginX + (CLEF_SPACES + keySigW + contentWidth) * s;
  const height = marginTop + strides.reduce((a, b) => a + b, 0) + 2 * s;

  // Page view: the strip cut at each system's above-furniture top (chord
  // symbols, marks band, volta, overlay lane) — page k's window starts exactly
  // where page k−1's ends, so every pixel of the engraving lands on exactly
  // one page. The cut mirrors the overlayTop maths in the render below.
  const systemCount = staff.systems.length;
  const systemCuts = [
    0,
    ...staff.systems.slice(1).map((_, i) => {
      const si = i + 1;
      return yTops[si] - (aboveNeeds[si] + 1.5) * s - (overlayHeights[si] ?? 0);
    }),
    height,
  ];
  const winStart = systemWindow
    ? Math.min(Math.max(0, systemWindow[0]), Math.max(0, systemCount - 1))
    : 0;
  const winEnd = systemWindow
    ? Math.max(winStart + 1, Math.min(systemCount, systemWindow[1]))
    : systemCount;
  const viewTop = systemWindow ? systemCuts[winStart] : 0;
  const viewHeight = systemWindow
    ? Math.max(1, (systemCuts[Math.min(winEnd, systemCount)] ?? height) - viewTop)
    : height;
  const spansSignature = systemCuts.map((c) => Math.round(c)).join(',');
  useEffect(() => {
    if (!onSystemSpans) return;
    const spans = staff.systems.map((_, si) => systemCuts[si + 1] - systemCuts[si]);
    onSystemSpans(spans);
    // The cuts array is rebuilt every render; the rounded signature is the
    // real identity, so the report fires only when the engraving moved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spansSignature, onSystemSpans]);

  const measureIdAtClient = (
    svg: SVGSVGElement,
    clientX: number,
    clientY: number,
  ): string | null => {
    const rect = svg.getBoundingClientRect();
    const pxX = (clientX - rect.left) * (rect.width > 0 ? width / rect.width : 1);
    // Windowed pages map the pointer back into WHOLE-strip coordinates, so the
    // system banding below needs no per-page cases.
    const pxY = (clientY - rect.top) * (rect.height > 0 ? viewHeight / rect.height : 1) + viewTop;
    let si = 0;
    for (let i = 0; i < staff.systems.length; i++) {
      const previousBottom =
        i === 0 ? 0 : yTops[i - 1] + staffHeight + sysStack[i - 1].block + voicingBlock;
      const boundary = i === 0 ? 0 : (previousBottom + yTops[i]) / 2;
      if (pxY >= boundary) si = i;
    }
    const headerW = CLEF_SPACES + keySigW + (si === 0 && staff.timeSig ? timeSigW : 0);
    const xSpaces = (pxX - (marginX + headerW * s)) / s;
    const sys = staff.systems[si];
    if (!sys) return null;
    const hitEpsilon = 1e-7;
    const mi = sys.measures.findIndex(
      (m, index) =>
        xSpaces >= m.x - hitEpsilon &&
        (xSpaces < m.x + m.width - hitEpsilon ||
          (index === sys.measures.length - 1 && xSpaces <= m.x + m.width + hitEpsilon)),
    );
    if (mi < 0) return null;
    return measureIdByBarIndex.get(sys.measures[mi].barIndex) ?? null;
  };

  const gesture = usePassageGesture({ onSelectPassage, onPassageGesture });
  const skipClickAfterDrag = useRef(false);
  const touchTap = useRef<{
    pointerId: number;
    x: number;
    y: number;
    moved: boolean;
  } | null>(null);
  const enableTouchTargets = touchNoteTargets && !!onSelectNote;

  const touchNoteAtClient = (
    svg: SVGSVGElement,
    clientX: number,
    clientY: number,
  ): ScoreEventHitTarget | null => {
    if (interactiveIndex === null) return null;
    const measureId = measureIdAtClient(svg, clientX, clientY);
    if (!measureId) return null;
    const si = staff.systems.findIndex((system) =>
      system.measures.some((measure) => measureIdByBarIndex.get(measure.barIndex) === measureId),
    );
    if (si < winStart || si >= winEnd) return null;
    const rect = svg.getBoundingClientRect();
    const scaleX = rect.width > 0 ? rect.width / width : 1;
    const scaleY = rect.height > 0 ? rect.height / viewHeight : 1;
    const y = (clientY - rect.top) / scaleY + viewTop;
    const drawnStaves = [staff, ...extraDrawn];
    const tops = [0, ...sysStack[si].tops].map((offset) => yTops[si] + offset);
    const centres = tops.map((top, index) => top + ((drawnStaves[index].staffLines - 1) * s) / 2);
    // A generous tap may never cross the midpoint into another authored staff.
    if (
      (interactiveIndex > 0 &&
        y < (centres[interactiveIndex - 1] + centres[interactiveIndex]) / 2) ||
      (interactiveIndex < staffCount - 1 &&
        y >= (centres[interactiveIndex] + centres[interactiveIndex + 1]) / 2)
    ) {
      return null;
    }
    const measures = drawnStaves[interactiveIndex].systems[si]?.measures;
    if (!measures) return null;
    const headerW = CLEF_SPACES + keySigW + (si === 0 && staff.timeSig ? timeSigW : 0);
    const targets: ScoreEventHitTarget[] = [];
    // An enlarged note target can cross a barline. Compare the whole active
    // staff/system so a closer neighbour is not excluded by the finger's bar.
    for (const measure of measures) {
      const x0 = marginX + (headerW + measure.x) * s;
      for (const note of measure.notes) {
        for (const head of note.heads ?? [
          { y: note.y, dx: 0, sourceHeadIndex: note.sourceHeadIndex },
        ]) {
          targets.push({
            id: note.sourceId,
            headIndex: head.sourceHeadIndex,
            x: rect.left + (x0 + (note.x + head.dx) * s) * scaleX,
            y: rect.top + (tops[interactiveIndex] + head.y * s - viewTop) * scaleY,
          });
        }
      }
      for (const rest of measure.rests) {
        if (!rest.sourceId) continue;
        targets.push({
          id: rest.sourceId,
          x: rect.left + (x0 + rest.x * s) * scaleX,
          y: rect.top + (tops[interactiveIndex] + rest.y * s - viewTop) * scaleY,
        });
      }
    }
    return nearestScoreHead(targets, clientX, clientY);
  };

  const handleClick = (e: MouseEvent<SVGSVGElement>) => {
    if (skipClickAfterDrag.current) {
      skipClickAfterDrag.current = false;
      return;
    }
    if (!onStaffClick && !onSelectMeasure && !onSelectPassage && !onTapMeasure) return;
    // A grip drawn over the staff (the arrangement overlay) is a control: its
    // tap plays it and must not also move the start cursor or the band.
    if (onTapMeasure && !onStaffClick && onControl(e.target, e.currentTarget)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    // The SVG is responsive (`max-width: 100%`) and is commonly smaller on
    // screen than its intrinsic viewBox. Pointer coordinates arrive in CSS
    // pixels, while all engraving geometry below is in viewBox units.
    const pxX = (e.clientX - rect.left) * (rect.width > 0 ? width / rect.width : 1);
    const pxY = (e.clientY - rect.top) * (rect.height > 0 ? viewHeight / rect.height : 1) + viewTop;
    // nearest system by vertical band
    let si = 0;
    for (let i = 0; i < staff.systems.length; i++) {
      const previousBottom =
        i === 0 ? 0 : yTops[i - 1] + staffHeight + sysStack[i - 1].block + voicingBlock;
      const boundary = i === 0 ? 0 : (previousBottom + yTops[i]) / 2;
      if (pxY >= boundary) si = i;
    }
    const y0 = yTops[si];
    // Form selection treats the score as one vertical bar column. Note entry,
    // by contrast, belongs to exactly one explicitly selected authored staff.
    // Midpoints between staff centres make the gap unambiguous while retaining
    // room for ledger-line clicks on either side.
    let clickStaffTop = y0;
    let clickStaffClef = options?.clef ?? 'treble8vb';
    if (onStaffClick) {
      if (interactiveIndex === null) return;
      const tops = [0, ...sysStack[si].tops].map((offset) => y0 + offset);
      const centres = tops.map((top) => top + staffHeight / 2);
      const lowerBound =
        interactiveIndex === 0
          ? -Infinity
          : (centres[interactiveIndex - 1] + centres[interactiveIndex]) / 2;
      const targetDrawn = interactiveIndex === 0 ? staff : extraDrawn[interactiveIndex - 1];
      const upperBound =
        interactiveIndex < staffCount - 1
          ? (centres[interactiveIndex] + centres[interactiveIndex + 1]) / 2
          : tops[interactiveIndex] + (targetDrawn.staffLines + 2) * s;
      if (pxY < lowerBound || pxY > upperBound) return;
      clickStaffTop = tops[interactiveIndex];
      if (interactiveIndex > 0) {
        clickStaffClef = extras[interactiveIndex - 1]?.clef ?? 'treble';
      }
    }
    const headerW = CLEF_SPACES + keySigW + (si === 0 && staff.timeSig ? timeSigW : 0);
    const xSpaces = (pxX - (marginX + headerW * s)) / s;
    const sys = staff.systems[si];
    if (!sys) return;
    const hitEpsilon = 1e-7;
    const mi = sys.measures.findIndex(
      (m, index) =>
        xSpaces >= m.x - hitEpsilon &&
        (xSpaces < m.x + m.width - hitEpsilon ||
          (index === sys.measures.length - 1 && xSpaces <= m.x + m.width + hitEpsilon)),
    );
    if (mi < 0) return;
    const m = sys.measures[mi];
    const measureId = measureIdByBarIndex.get(m.barIndex);
    if (measureId && onTapMeasure && !onStaffClick && !e.shiftKey) {
      onTapMeasure(measureId);
      return;
    }
    if (measureId) {
      if (e.shiftKey && onSelectPassage) onSelectPassage(measureId, measureId, { extend: true });
      else onSelectMeasure?.(measureId);
    }
    if (e.shiftKey && onSelectPassage) return;
    onClearPassage?.();
    if (onStaffClick) {
      onStaffClick({
        barIndex: m.barIndex,
        staffIndex: interactiveIndex!,
        beatFraction: beatFractionAtXInMeasure(m, xSpaces - m.x),
        midi: pitchFromStaffY((pxY - clickStaffTop) / s, clickStaffClef),
      });
    }
  };

  const onScorePointerDown = (e: PointerEvent<SVGSVGElement>) => {
    skipClickAfterDrag.current = false;
    if (enableTouchTargets && e.pointerType === 'touch') {
      if (e.isPrimary === false) {
        touchTap.current = null;
        gesture.down(e, null);
        skipClickAfterDrag.current = true;
        return;
      }
      touchTap.current = { pointerId: e.pointerId, x: e.clientX, y: e.clientY, moved: false };
    }
    if (!onSelectPassage) return;
    gesture.down(e, measureIdAtClient(e.currentTarget, e.clientX, e.clientY));
  };
  const onScorePointerMove = (e: PointerEvent<SVGSVGElement>) => {
    const tap = touchTap.current;
    if (
      tap &&
      tap.pointerId === e.pointerId &&
      (Math.abs(e.clientX - tap.x) >= 8 || Math.abs(e.clientY - tap.y) >= 8)
    ) {
      tap.moved = true;
    }
    if (!onSelectPassage) return;
    const svg = e.currentTarget;
    gesture.move(e, () => measureIdAtClient(svg, e.clientX, e.clientY));
  };
  const onScorePointerUp = (e: PointerEvent<SVGSVGElement>) => {
    const tap = touchTap.current;
    touchTap.current = null;
    if (onSelectPassage) gesture.up(e);
    if (gesture.clickIsGesture()) {
      skipClickAfterDrag.current = true;
      return;
    }
    if (tap && tap.pointerId === e.pointerId) {
      if (tap.moved || Math.abs(e.clientX - tap.x) >= 8 || Math.abs(e.clientY - tap.y) >= 8) {
        skipClickAfterDrag.current = true;
      } else if (!e.shiftKey && enableTouchTargets) {
        // A direct chord tap already has an exact target. Nearest-note
        // assistance must not redirect it to a note in the staff above.
        if (e.target instanceof Element && e.target.closest('[data-chord-id][role="button"]'))
          return;
        const noteId = touchNoteAtClient(e.currentTarget, e.clientX, e.clientY);
        if (noteId) {
          skipClickAfterDrag.current = true;
          onSelectNote?.(noteId.id, { shiftKey: false, headIndex: noteId.headIndex });
          return;
        }
      }
    }
  };

  const g = (cp: number) => glyphChar(cp);

  /** One staff's measure content: beams, notes, rests, ties, barlines. Shared
   *  between the treble and bass staves (chord symbols stay treble-only). */
  const renderMeasures = (
    measures: DrawnMeasure[],
    px: (x: number) => number,
    py: (y: number) => number,
    interactive: boolean,
    staffTopY: number,
  ): ReactNode => (
    <>
      {measures.map((m, mi) => {
        const ox = m.x;
        return (
          <g
            key={mi}
            data-measure={m.barIndex}
            data-measure-id={measureIdByBarIndex.get(m.barIndex)}
            data-measure-x={px(ox)}
            data-measure-width={m.width * s}
            data-entry-beat-one-x={px(ox + m.emptyBeat1X)}
            data-entry-beat-one-capture-x={px(ox + m.emptyBeat1CaptureX)}
          >
            {m.beams.map((b, bi) => (
              <line
                key={bi}
                data-beam={b.level ?? 1}
                x1={px(ox + b.x1)}
                y1={py(b.y1)}
                x2={px(ox + b.x2)}
                y2={py(b.y2)}
                stroke="currentColor"
                strokeWidth={ENGRAVING.beamThickness * s * (b.grace ? ENGRAVING.graceScale : 1)}
                strokeLinecap="butt"
              />
            ))}

            {m.dynamics.map((d, di) => (
              <text
                key={`dyn-${di}`}
                x={px(ox + d.x)}
                y={py(dynamicPositions.get(d) ?? d.y)}
                fontFamily="Bravura"
                fontSize={emSize}
                textAnchor="middle"
                dominantBaseline="central"
                fill="currentColor"
                data-dynamic
              >
                {d.glyphs.map((gl) => g(gl)).join('')}
              </text>
            ))}

            {m.texts.map((t, ti) => (
              <text
                key={`words-${ti}`}
                x={px(ox + t.x)}
                y={py(textDodge.get(t) ?? t.y)}
                fontSize={0.42 * emSize}
                fontFamily={TEXT_FONT}
                fontStyle="italic"
                textAnchor="start"
                dominantBaseline="central"
                fill="currentColor"
                data-staff-text
              >
                {t.text}
              </text>
            ))}

            {m.barRepeat && (
              <text
                x={px(ox + m.width / 2)}
                y={staffTopY + 2 * s}
                fontFamily="Bravura"
                fontSize={emSize}
                textAnchor="middle"
                dominantBaseline="central"
                fill="currentColor"
                data-bar-repeat={m.barRepeat}
              >
                {g(m.barRepeat === 1 ? SMUFL.repeat1Bar : SMUFL.repeat2Bars)}
              </text>
            )}

            {m.tuplets.map((t, ti) => (
              <g key={`tup-${ti}`} data-tuplet={t.label}>
                {t.x1 != null && t.x2 != null && (
                  // Square bracket when the beam does not show the whole group:
                  // horizontal limbs either side of the figure, hooks
                  // pointing at the notes.
                  <path
                    d={(() => {
                      const hook = (t.below ? -1 : 1) * TUPLET_ENGRAVING.hookLength * s;
                      const gap = TUPLET_ENGRAVING.numberGap * s;
                      const x1 = px(ox + t.x1);
                      const x2 = px(ox + t.x2);
                      const xm = px(ox + t.x);
                      const y = py(t.y);
                      return `M ${x1} ${y + hook} L ${x1} ${y} L ${xm - gap} ${y} M ${xm + gap} ${y} L ${x2} ${y} L ${x2} ${y + hook}`;
                    })()}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={0.13 * s}
                  />
                )}
                <text
                  x={px(ox + t.x)}
                  y={py(t.y)}
                  fontFamily="Georgia, 'Times New Roman', serif"
                  fontStyle="italic"
                  fontWeight={600}
                  fontSize={TUPLET_ENGRAVING.fontSize * s}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="currentColor"
                >
                  {t.label}
                </text>
              </g>
            ))}

            {m.notes.map((note) => {
              // Grace/cue notes draw reduced; the scales are shared with the
              // engraver's stem geometry (ENGRAVING) so stems meet the heads.
              const headScale = note.grace
                ? ENGRAVING.graceScale
                : note.cue
                  ? ENGRAVING.cueScale
                  : 1;
              const noteEm = emSize * headScale;
              return (
                <g
                  key={note.sourceId}
                  data-note={note.sourceId}
                  data-grace={note.grace || undefined}
                  data-selected={(interactive && note.sourceId === selectedId) || undefined}
                  style={{ cursor: interactive && onSelectNote ? 'pointer' : undefined }}
                  onClick={
                    interactive && onSelectNote
                      ? (e) => {
                          e.stopPropagation();
                          const svg = e.currentTarget.ownerSVGElement;
                          const hit = svg ? touchNoteAtClient(svg, e.clientX, e.clientY) : null;
                          onSelectNote(hit?.id ?? note.sourceId, {
                            shiftKey: e.shiftKey,
                            headIndex: hit?.headIndex,
                          });
                        }
                      : undefined
                  }
                  color={
                    interactive && note.sourceId === selectedId
                      ? 'var(--accent, #d4537e)'
                      : undefined
                  }
                >
                  {/* A chord draws every head; a single note is a one-head stack. */}
                  {(
                    note.heads ?? [
                      {
                        y: note.y,
                        sourceHeadIndex: note.sourceHeadIndex,
                        dx: 0,
                        accidental: note.accidental,
                        accidentalDx: note.accidentalDx,
                        ledger: note.ledger,
                      },
                    ]
                  ).map((head, hi) => (
                    <g
                      key={head.sourceHeadIndex ?? hi}
                      data-notehead={head.sourceHeadIndex ?? hi}
                      data-head-selected={
                        (interactive &&
                          note.sourceId === selectedId &&
                          head.sourceHeadIndex === selectedHeadIndex) ||
                        undefined
                      }
                      color={
                        interactive && note.sourceId === selectedId && selectedHeadIndex != null
                          ? head.sourceHeadIndex === selectedHeadIndex
                            ? '#2563eb'
                            : 'var(--ink, #222)'
                          : undefined
                      }
                      onClick={
                        interactive && onSelectNote
                          ? (e) => {
                              e.stopPropagation();
                              const svg = e.currentTarget.ownerSVGElement;
                              const hit = svg ? touchNoteAtClient(svg, e.clientX, e.clientY) : null;
                              onSelectNote(hit?.id ?? note.sourceId, {
                                shiftKey: e.shiftKey,
                                headIndex: hit?.headIndex ?? head.sourceHeadIndex,
                              });
                            }
                          : undefined
                      }
                    >
                      {head.ledger.map((ly, k) => (
                        <line
                          key={k}
                          data-ledger
                          x1={
                            px(ox + note.x + head.dx) -
                            ledgerHalfWidth(note.notehead, headScale) * s
                          }
                          x2={
                            px(ox + note.x + head.dx) +
                            ledgerHalfWidth(note.notehead, headScale) * s
                          }
                          y1={py(ly)}
                          y2={py(ly)}
                          stroke="currentColor"
                          strokeWidth={ENGRAVING.staffLineThickness * s}
                        />
                      ))}
                      {head.accidental !== undefined && (
                        <text
                          x={px(ox + note.x + (head.accidentalDx ?? -1.0 * headScale))}
                          y={py(head.y)}
                          fontFamily="Bravura"
                          fontSize={noteEm}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fill="currentColor"
                          data-accidental={head.accidental}
                        >
                          {g(head.accidental)}
                        </text>
                      )}
                      <text
                        x={px(ox + note.x + head.dx)}
                        y={py(head.y)}
                        data-head-glyph
                        fontFamily="Bravura"
                        fontSize={noteEm}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fill="currentColor"
                      >
                        {g(note.notehead)}
                      </text>
                      {Array.from({ length: note.dots }, (_, di) => (
                        <text
                          key={di}
                          x={
                            px(ox + note.x + head.dx) +
                            (((note.noteheadWidth ?? ENGRAVING.noteheadWidth) / 2) * headScale + 0.4 + di * 0.5) * s
                          }
                          y={py(head.y)}
                          fontFamily="Bravura"
                          fontSize={noteEm}
                          dominantBaseline="central"
                          fill="currentColor"
                          data-augmentation-dot
                        >
                          {g(SMUFL.augmentationDot)}
                        </text>
                      ))}
                    </g>
                  ))}
                  {note.stem && (
                    <line
                      x1={px(ox + note.stem.x)}
                      x2={px(ox + note.stem.x)}
                      y1={py(note.stem.y1)}
                      y2={py(note.stem.y2)}
                      stroke="currentColor"
                      strokeWidth={ENGRAVING.stemThickness * s}
                    />
                  )}
                  {note.ornaments?.map((o, oi) => (
                    <text
                      key={`orn-${oi}`}
                      x={px(ox + note.x)}
                      y={py(o.y)}
                      fontFamily="Bravura"
                      fontSize={noteEm}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill="currentColor"
                      data-ornament
                    >
                      {g(o.glyph)}
                    </text>
                  ))}
                  {note.artics?.map((a, ai) => (
                    <text
                      key={`art-${ai}`}
                      x={px(ox + note.x)}
                      y={py(a.y)}
                      fontFamily="Bravura"
                      fontSize={noteEm}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill="currentColor"
                      data-artic
                    >
                      {g(a.glyph)}
                    </text>
                  ))}
                  {note.flag !== undefined && note.stem && (
                    <text
                      x={px(ox + note.stem.x)}
                      y={py(note.stem.y2)}
                      fontFamily="Bravura"
                      fontSize={noteEm}
                      dominantBaseline="central"
                      fill="currentColor"
                    >
                      {g(note.flag)}
                    </text>
                  )}
                </g>
              );
            })}

            {m.rests.map((r, ri) => (
              <g key={r.sourceId ?? ri}>
                <text
                  x={px(ox + r.x)}
                  y={py(r.y)}
                  fontFamily="Bravura"
                  fontSize={r.cue ? emSize * ENGRAVING.cueScale : emSize}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="currentColor"
                  data-rest={r.sourceId || undefined}
                  data-selected={
                    (interactive && r.sourceId != null && r.sourceId === selectedId) || undefined
                  }
                  style={{
                    cursor: interactive && r.sourceId && onSelectNote ? 'pointer' : undefined,
                    color:
                      interactive && r.sourceId === selectedId
                        ? 'var(--accent, #d4537e)'
                        : undefined,
                  }}
                  onClick={
                    interactive && r.sourceId && onSelectNote
                      ? (e) => {
                          e.stopPropagation();
                          onSelectNote(r.sourceId!, { shiftKey: e.shiftKey });
                        }
                      : undefined
                  }
                >
                  {g(r.glyph)}
                </text>
                {r.ornaments?.map((ornament, index) => (
                  <text
                    key={`orn-${index}`}
                    x={px(ox + r.x)}
                    y={py(ornament.y)}
                    fontFamily="Bravura"
                    fontSize={r.cue ? emSize * ENGRAVING.cueScale : emSize}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="currentColor"
                    data-ornament
                  >
                    {g(ornament.glyph)}
                  </text>
                ))}
              </g>
            ))}

            {(() => {
              // Closing barline. Repeat/final pairs: thin line, then the
              // thick line with its right edge on the bar edge; repeat adds
              // the two dots in staff spaces 2 and 3.
              const thin = ENGRAVING.staffLineThickness * s;
              const vline = (x: number, w: number, key: string) => (
                <line
                  key={key}
                  x1={px(ox + x)}
                  x2={px(ox + x)}
                  y1={staffTopY}
                  y2={staffTopY + staffHeight}
                  stroke="currentColor"
                  strokeWidth={w}
                />
              );
              const dots = (x: number, key: string) => (
                <g key={key} data-repeat-dots>
                  <circle
                    cx={px(ox + x)}
                    cy={staffTopY + 1.5 * s}
                    r={0.2 * s}
                    fill="currentColor"
                  />
                  <circle
                    cx={px(ox + x)}
                    cy={staffTopY + 2.5 * s}
                    r={0.2 * s}
                    fill="currentColor"
                  />
                </g>
              );
              const parts: ReactNode[] = [];
              if (m.barline === 'final' || m.barline === 'repeat') {
                if (m.barline === 'repeat') parts.push(dots(m.width - 1.55, 'cd'));
                parts.push(vline(m.width - 0.9, thin, 'ct'));
                parts.push(vline(m.width - 0.25, 0.5 * s, 'ck'));
              } else if (m.barline === 'double') {
                parts.push(vline(m.width - 0.5, thin, 'ct'));
                parts.push(vline(m.width, thin, 'ck'));
              } else {
                parts.push(vline(m.width, thin, 'ck'));
              }
              // An overfull bar (its written durations exceed the meter — a
              // legal mid-edit state under the Guitar Pro model) is flagged
              // in red at its closing barline until the durations add up.
              if (m.overfull) {
                parts.push(
                  <g key="ovf" data-overfull>
                    <line
                      x1={px(ox + m.width)}
                      x2={px(ox + m.width)}
                      y1={staffTopY - 0.9 * s}
                      y2={staffTopY + staffHeight + 0.9 * s}
                      stroke="#c0392b"
                      strokeWidth={2 * thin}
                    />
                    <text
                      x={px(ox + m.width - 0.35)}
                      y={staffTopY - 1.3 * s}
                      textAnchor="end"
                      fontSize={2.4 * s}
                      fill="#c0392b"
                      style={{ fontFamily: TEXT_FONT, fontWeight: 700 }}
                    >
                      +
                    </text>
                    <title>
                      This bar holds more than its time signature — shorten a note to fit
                    </title>
                  </g>,
                );
              }
              // Opening decoration (forward repeat / double) at the bar start.
              if (m.openBarline === 'repeat') {
                parts.push(vline(0.25, 0.5 * s, 'ok'));
                parts.push(vline(0.9, thin, 'ot'));
                parts.push(dots(1.55, 'od'));
              } else if (m.openBarline === 'double') {
                parts.push(vline(0.5, thin, 'ot'));
              }
              return parts;
            })()}
          </g>
        );
      })}
    </>
  );

  /** The sequential-entry caret follows the selected authored staff. Keeping
   * it outside renderMeasures ensures it sits above that staff's notation and
   * never appears simultaneously on another staff in the stack. */
  const renderEntryCaret = (
    measures: DrawnMeasure[],
    px: (x: number) => number,
    staffTopY: number,
    staffIndex: number,
  ): ReactNode => {
    if (entryCursor?.barIndex == null) return null;
    const measure = measures.find((candidate) => candidate.barIndex === entryCursor.barIndex);
    if (!measure) return null;
    // xAtBeatInMeasure clamps invalid values to the written bar, which keeps a
    // stale cursor harmless while form edits and rerendering settle.
    const x = px(measure.x + xAtBeatInMeasure(measure, entryCursor.beat));
    const top = staffTopY - 0.7 * s;
    const bottom = staffTopY + staffHeight + 0.7 * s;
    const cap = 0.55 * s;
    const ink = 'var(--score-entry-cursor, var(--accent, #d4537e))';
    return (
      <g
        data-entry-cursor
        data-bar-index={entryCursor.barIndex}
        data-beat={entryCursor.beat}
        data-staff-index={staffIndex}
        pointerEvents="none"
        aria-hidden="true"
      >
        {/* A pale keyline keeps the caret legible through a notehead, beam or
            staff line; the coloured core and end caps distinguish it from the
            playback cursor. */}
        <line
          x1={x}
          x2={x}
          y1={top}
          y2={bottom}
          stroke="var(--surface, #fff)"
          strokeWidth={Math.max(4, 0.38 * s)}
          opacity={0.85}
        />
        <line
          x1={x}
          x2={x}
          y1={top}
          y2={bottom}
          stroke={ink}
          strokeWidth={Math.max(2.5, 0.21 * s)}
          strokeLinecap="round"
          data-entry-cursor-line
        />
        <path
          d={`M ${x - cap} ${top - cap * 0.75} L ${x + cap} ${top - cap * 0.75} L ${x} ${top} Z`}
          fill={ink}
        />
        <path
          d={`M ${x - cap} ${bottom + cap * 0.75} L ${x + cap} ${bottom + cap * 0.75} L ${x} ${bottom} Z`}
          fill={ink}
        />
      </g>
    );
  };

  /** Staff lines + clef + key signature (+ time signature on system 1). */
  const renderStaffChrome = (
    drawn: DrawnStaff,
    sysWidth: number,
    contentX0: number,
    topY: number,
    si: number,
  ): ReactNode => {
    const py = (yb: number) => topY + yb * s;
    const keySigX = (i: number) => marginX + (CLEF_SPACES + KEYSIG_LEAD + i * KEYSIG_STEP) * s;
    const timeSigX = marginX + (CLEF_SPACES + keySigW + timeSigW / 2) * s;
    return (
      <>
        {Array.from({ length: drawn.staffLines }, (_, li) => (
          <line
            key={li}
            x1={marginX}
            x2={contentX0 + sysWidth * s}
            y1={topY + li * s}
            y2={topY + li * s}
            stroke="currentColor"
            strokeWidth={ENGRAVING.staffLineThickness * s}
            data-staff-line={li}
          />
        ))}
        <text
          x={marginX + 0.2 * s}
          y={py(drawn.clefY)}
          data-clef={drawn.clefName}
          fontFamily="Bravura"
          fontSize={emSize}
          fill="currentColor"
        >
          {g(drawn.clef)}
        </text>
        {drawn.keySignature.map((acc, i) => (
          <text
            key={i}
            x={keySigX(i)}
            y={py(acc.y)}
            fontFamily="Bravura"
            fontSize={emSize}
            textAnchor="middle"
            dominantBaseline="central"
            fill="currentColor"
          >
            {g(acc.glyph)}
          </text>
        ))}
        {drawn.timeSig && si === 0 && (
          <g fontFamily="Bravura" fontSize={emSize} fill="currentColor" textAnchor="middle">
            {/* The book's convention: 4/4 prints the common-time C, 2/2 cut time. */}
            {drawn.timeSig[0] === 4 && drawn.timeSig[1] === 4 ? (
              <text x={timeSigX} y={py(2)}>
                {g(SMUFL.timeSigCommon)}
              </text>
            ) : drawn.timeSig[0] === 2 && drawn.timeSig[1] === 2 ? (
              <text x={timeSigX} y={py(2)}>
                {g(SMUFL.timeSigCutCommon)}
              </text>
            ) : (
              <>
                <text x={timeSigX} y={py(1)} data-time-sig="numerator">
                  {timeSigNumber(drawn.timeSig[0])}
                </text>
                <text x={timeSigX} y={py(3)} data-time-sig="denominator">
                  {timeSigNumber(drawn.timeSig[1])}
                </text>
              </>
            )}
          </g>
        )}
      </>
    );
  };

  const renderLyrics = (drawn: DrawnStaff, staffTopY: (si: number) => number): ReactNode => {
    if (lyricLanes.every((l) => l.verses === 0)) return null;
    const headerWOf = (si: number) =>
      CLEF_SPACES + keySigW + (si === 0 && drawn.timeSig ? timeSigW : 0);
    return drawn.systems.map((sys, si) => {
      const lane = lyricLanes[si];
      if (lane.verses === 0) return null;
      const contentX0 = marginX + headerWOf(si) * s;
      const y0 = staffTopY(si);
      const laneY = (verse: number) => y0 + (lane.base + verse * 1.7) * s;
      // System-wide syllable list per verse, in x order, for hyphen midpoints.
      const byVerse = new Map<number, { x: number; text: string; hyphen?: boolean }[]>();
      for (const m of sys.measures) {
        for (const l of m.lyrics) {
          const list = byVerse.get(l.verse) ?? [];
          list.push({ x: contentX0 + (m.x + l.x) * s, text: l.text, hyphen: l.hyphen });
          byVerse.set(l.verse, list);
        }
      }
      return (
        <g key={`lyr-${si}`} data-lyrics>
          {[...byVerse.entries()].flatMap(([verse, list]) => {
            list.sort((a, b) => a.x - b.x);
            return list.flatMap((syl, i) => {
              const out = [
                <text
                  key={`v${verse}-${i}`}
                  x={syl.x}
                  y={laneY(verse)}
                  fontSize={0.45 * emSize}
                  fontFamily={TEXT_FONT}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="currentColor"
                >
                  {syl.text}
                </text>,
              ];
              if (syl.hyphen) {
                const next = list[i + 1];
                const hx = next ? (syl.x + next.x) / 2 : syl.x + 1.6 * s;
                out.push(
                  <text
                    key={`v${verse}-${i}-hy`}
                    x={hx}
                    y={laneY(verse)}
                    fontSize={0.45 * emSize}
                    fontFamily={TEXT_FONT}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="currentColor"
                  >
                    -
                  </text>,
                );
              }
              return out;
            });
          })}
        </g>
      );
    });
  };

  /**
   * Solved slur curves for one staff. Slurs anchor on note ids, so they
   * survive any layout change; a slur whose endpoints landed on different
   * systems splits into an arc out to the right margin and an arc in from
   * the left. The arc sits on the side OPPOSITE the first note's stem
   * (engraving convention). Computed once per staff — the paths draw from
   * these, and struck staff texts dodge past them (slurTextDodge).
   */
  const positionSlurs = (curves: StaffSlurCurve[], staffTopY: (si: number) => number) =>
    curves.map(({ c, ...rest }) => {
      const x0 =
        marginX + (CLEF_SPACES + keySigW + (rest.si === 0 && staff.timeSig ? timeSigW : 0)) * s;
      const y0 = staffTopY(rest.si);
      return {
        ...rest,
        c: {
          ax1: x0 + c.ax1 * s,
          ax2: x0 + c.ax2 * s,
          cx1: x0 + c.cx1 * s,
          cx2: x0 + c.cx2 * s,
          ay1: y0 + c.ay1 * s,
          ay2: y0 + c.ay2 * s,
          cy1: y0 + c.cy1 * s,
          cy2: y0 + c.cy2 * s,
        },
      };
    });

  const renderTies = (ties: TieSegment[], staffTopY: (si: number) => number) =>
    ties
      .filter((t) => t.si >= winStart && t.si < winEnd)
      .map((t) => {
        const x0 =
          marginX + (CLEF_SPACES + keySigW + (t.si === 0 && staff.timeSig ? timeSigW : 0)) * s;
        const y0 = staffTopY(t.si);
        return (
          <path
            key={`tie-${t.fromId}-${t.head}-${t.segment}`}
            data-tie
            data-tie-from={t.fromId}
            data-tie-to={t.toId}
            data-tie-head={t.head}
            data-tie-segment={t.segment}
            data-tie-system={t.si}
            d={`M ${x0 + t.x1 * s} ${y0 + t.y1 * s} Q ${x0 + t.cx * s} ${y0 + t.cy * s} ${x0 + t.x2 * s} ${y0 + t.y2 * s}`}
            fill="none"
            stroke="currentColor"
            strokeWidth={ENGRAVING.staffLineThickness * s * 1.4}
            pointerEvents="none"
          />
        );
      });

  const renderSlurs = (curves: (StaffSlurCurve & { c: SlurCurve })[]): ReactNode =>
    curves
      .filter((c) => c.si >= winStart && c.si < winEnd)
      .map(({ key, c, si, fromId, toId }) => (
        <path
          key={key}
          d={`M ${c.ax1} ${c.ay1} C ${c.cx1} ${c.cy1} ${c.cx2} ${c.cy2} ${c.ax2} ${c.ay2}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={ENGRAVING.staffLineThickness * s * 1.4}
          pointerEvents="none"
          data-slur
          data-slur-system={si}
          data-slur-from={fromId}
          data-slur-to={toId}
        />
      ));

  const renderHairpins = (hairpins: StaffHairpin[], staffTopY: (si: number) => number): ReactNode =>
    hairpins
      .filter((h) => h.si >= winStart && h.si < winEnd)
      .map((h) => {
        const x0 =
          marginX + (CLEF_SPACES + keySigW + (h.si === 0 && staff.timeSig ? timeSigW : 0)) * s;
        const y = staffTopY(h.si) + h.y * s;
        return (
          <g key={h.key} data-hairpin data-hairpin-system={h.si}>
            {[-1, 1].map((side) => (
              <line
                key={side}
                x1={x0 + h.x1 * s}
                y1={y + side * h.open1 * s}
                x2={x0 + h.x2 * s}
                y2={y + side * h.open2 * s}
                stroke="currentColor"
                strokeWidth={ENGRAVING.staffLineThickness * s}
                pointerEvents="none"
              />
            ))}
          </g>
        );
      });

  // Place the locally solved curves after staff and page spacing is known.
  const mainSlurCurves = positionSlurs(localMainSlurs, (si) => yTops[si]);
  const extraSlurCurves = extraDrawn.map((_, k) =>
    positionSlurs(localExtraSlurs[k], (si) => yTops[si] + sysStack[si].tops[k]),
  );

  return (
    <div className={className}>
      <style>{BRAVURA_FACE}</style>
      <svg
        width={width}
        height={viewHeight}
        viewBox={`0 ${viewTop} ${width} ${viewHeight}`}
        role={onSelectChord ? 'group' : 'img'}
        aria-label={ariaLabel ?? `Score: ${song.title}`}
        style={{
          maxWidth: '100%',
          height: 'auto', // scale proportionally when clamped — no letterboxing
          touchAction: enableTouchTargets ? 'pan-y pinch-zoom' : undefined,
          color: 'var(--score-ink, currentColor)',
          cursor: onStaffClick
            ? 'crosshair'
            : onSelectMeasure || onSelectPassage
              ? 'pointer'
              : 'default',
        }}
        data-testid="score-svg"
        data-touch-note-targets={enableTouchTargets || undefined}
        ref={onSelectPassage ? gesture.surfaceRef : undefined}
        onClickCapture={(e) => {
          // A glyph's own click handler runs before the SVG bubble handler.
          // Consume the compatibility click here after touch selection/scroll
          // or passage dragging so it cannot select a note a second time.
          if (!skipClickAfterDrag.current && !gesture.clickIsGesture()) return;
          skipClickAfterDrag.current = false;
          e.stopPropagation();
          e.preventDefault();
        }}
        onClick={handleClick}
        onPointerDown={onSelectPassage || enableTouchTargets ? onScorePointerDown : undefined}
        onPointerMove={onSelectPassage || enableTouchTargets ? onScorePointerMove : undefined}
        onPointerUp={onSelectPassage || enableTouchTargets ? onScorePointerUp : undefined}
        onPointerCancel={
          onSelectPassage || enableTouchTargets
            ? () => {
                if (touchTap.current) skipClickAfterDrag.current = true;
                touchTap.current = null;
                gesture.cancel();
              }
            : undefined
        }
      >
        {song.tempoText && (
          // Alphabetic baseline (not hanging) so the Bravura metronome note
          // anchors correctly — SMuFL glyphs sit on the text baseline.
          <text
            x={marginX}
            y={1.6 * s + tempoTextSize * 0.8}
            fontFamily="var(--score-text-font, Georgia, 'Times New Roman', serif)"
            fontWeight={700}
            fontSize={tempoTextSize}
            fill="currentColor"
            data-tempo-text
          >
            {song.tempoText}
            {song.tempoBpm ? (
              <>
                {' ('}
                <tspan fontFamily="Bravura" fontSize={1.7 * s}>
                  {g(
                    song.timeSignature[1] === 2
                      ? 0xeca3 /* half */
                      : song.timeSignature[1] === 8
                        ? 0xeca7 /* eighth */
                        : 0xeca5 /* quarter */,
                  )}
                </tspan>
                {` = ${song.tempoBpm})`}
              </>
            ) : null}
          </text>
        )}
        {staff.systems.map((sys, si) => {
          // Page view: out-of-window systems don't render at all (the sibling
          // layers — slurs, lyrics, hairpins — draw absolutely and clip).
          if (si < winStart || si >= winEnd) return null;
          const y0 = yTops[si]; // top staff line, px
          const headerW = CLEF_SPACES + keySigW + (si === 0 && staff.timeSig ? timeSigW : 0);
          const contentX0 = marginX + headerW * s;
          const px = (x: number) => contentX0 + x * s;
          const py = (yBelowTop: number) => y0 + yBelowTop * s;
          const stackTops = sysStack[si].tops;
          /** Bottom of the system's LAST stave (px below y0): the main staff,
           * the stacked extra staves (grand staff, full score, a second part),
           * or the studio's lower voicing/tab staff — whichever is lowest.
           * Lyric lanes deliberately stay outside: the wash covers staves. */
          const stackBottom = hasLower
            ? staffHeight + sysStack[si].block + voicingGap + lowerHeight
            : stackTops.length > 0
              ? stackTops[stackTops.length - 1] + staffHeight
              : staffHeight;
          /** One bar's wash geometry — selection and the playing bar share it,
           * so the two highlights can never drift apart. It spans the WHOLE
           * staff stack, like the playhead line: a bar plays on every stave at
           * once, and washing only the top one read as "only the top stave
           * lights up" on a two-part score (user report, Night in Tunisia's
           * Lead + Bass). A single-staff system keeps the exact old geometry. */
          const measureWashRect = (measure: (typeof sys.measures)[number]) => ({
            x: px(measure.x) + 0.12 * s,
            y: y0 - 0.65 * s,
            width: Math.max(0, measure.width * s - 0.24 * s),
            height: stackBottom + 1.3 * s,
            rx: 0.3 * s,
          });
          const overlayTop = y0 - (aboveNeeds[si] + 1.5) * s - overlayHeights[si];
          return (
            <g key={si} data-system={si}>
              {overlayHeights[si] > 0 &&
                renderSystemOverlay?.({
                  systemIndex: si,
                  measures: sys.measures,
                  top: overlayTop,
                  height: overlayHeights[si],
                  spaceSize: s,
                  xAtBeat: (barIndex, beat) => {
                    const measure = sys.measures.find(
                      (candidate) => candidate.barIndex === barIndex,
                    );
                    return measure ? px(measure.x + xAtBeatInMeasure(measure, beat)) : null;
                  },
                  left: marginX,
                  right: contentX0 + sys.width * s,
                })}
              {underlayHeights[si] > 0 &&
                renderSystemUnderlay?.({
                  systemIndex: si,
                  measures: sys.measures,
                  // Below everything the system draws: staff, low stems, lyric
                  // lanes and stacked staves — the same extent the stride pays.
                  top:
                    y0 +
                    staffHeight +
                    Math.max(sysStack[si].block + voicingBlock, mainDrop[si] * s) +
                    0.8 * s,
                  height: underlayHeights[si],
                  spaceSize: s,
                  xAtBeat: (barIndex, beat) => {
                    const measure = sys.measures.find(
                      (candidate) => candidate.barIndex === barIndex,
                    );
                    return measure ? px(measure.x + xAtBeatInMeasure(measure, beat)) : null;
                  },
                  left: marginX,
                  right: contentX0 + sys.width * s,
                })}
              {sys.measures.map((measure) => {
                const measureId = measureIdByBarIndex.get(measure.barIndex);
                const highlighted =
                  !!measureId &&
                  (measureId === selectedMeasureId || !!selectedMeasureIds?.has(measureId));
                if (!highlighted || !measureId) return null;
                return (
                  <rect
                    key={`measure-selection-${measureId}`}
                    {...measureWashRect(measure)}
                    fill="var(--selection-tint)"
                    fillOpacity={1}
                    stroke="none"
                    pointerEvents="none"
                    aria-hidden="true"
                    data-measure-selection={measureId}
                  />
                );
              })}
              {/* The playing bar wears the SAME wash the chart's does — the
                  picked playhead tint applies to every surface (user ask,
                  Aug 2026), with the vertical line kept as the beat-accurate
                  indicator. Drawn here, behind the staff chrome, in the
                  selection rect's exact geometry so the two washes cannot
                  drift apart. */}
              {playheadBeat != null &&
                (() => {
                  const playing = sys.measures.find((mm) => {
                    const start = barStartByIndex.get(mm.barIndex) ?? 0;
                    return playheadBeat >= start && playheadBeat < start + mm.beats;
                  });
                  if (!playing) return null;
                  return (
                    <rect
                      {...measureWashRect(playing)}
                      fill="var(--playhead-tint)"
                      pointerEvents="none"
                      aria-hidden="true"
                      data-measure-playing
                    />
                  );
                })()}
              {renderStaffChrome(staff, sys.width, contentX0, y0, si)}

              {/* Bar numbers: small and quiet, tucked above the top line at
                  the bar's left edge. Baseline −0.35 spaces at 1.15 spaces
                  tall keeps the ink under y = −1.2, where the chord row's
                  baseline begins, so the two never touch by construction. */}
              {barNumbers &&
                sys.measures.map((m) => {
                  const n = m.barIndex + (anacrusis ? 0 : 1);
                  if (n <= 0) return null;
                  return (
                    <text
                      key={`bn-${m.barIndex}`}
                      data-bar-number={n}
                      x={px(m.x) + 0.15 * s}
                      y={y0 - 0.35 * s}
                      fontSize={1.15 * s}
                      textAnchor="start"
                      fill="currentColor"
                      fillOpacity={0.6}
                      fontFamily="var(--font-sans, sans-serif)"
                    >
                      {n}
                    </text>
                  );
                })}

              {/* Chord symbols (treble only) — lead-sheet typesetting: big
                  root, quality/extensions raised small, slash bass back at
                  full size (chordLabel.ts owns the same width maths). */}
              {sys.measures.map((m, mi) => (
                <g key={`cs-${mi}`}>
                  {m.chordSymbols.map((c, ci) => {
                    const parts = parseChordLabel(c.text, options?.chordSymbolStyle);
                    const interactive = !!onSelectChord && !!c.sourceId;
                    const selected = !!c.sourceId && c.sourceId === selectedChordId;
                    const x = px(m.x + c.x);
                    const y = y0 + chordBaseline[si] * s;
                    const symbolWidth = chordLabelSpaces(c.text, options?.chordSymbolStyle) * s;
                    const previous = m.chordSymbols[ci - 1];
                    const next = m.chordSymbols[ci + 1];
                    const previousX = previous ? px(m.x + previous.x) : px(m.x);
                    const previousRight = previous
                      ? previousX + chordLabelSpaces(previous.text, options?.chordSymbolStyle) * s
                      : previousX;
                    const nextX = next ? px(m.x + next.x) : px(m.x + m.width);
                    const currentRight = x + symbolWidth;
                    // Keep generous hit targets disjoint and inside their bar. A
                    // short symbol such as C must not steal clicks from its
                    // neighbour or from the following barline.
                    const leftLimit = previous
                      ? previousRight <= x
                        ? (previousRight + x) / 2
                        : (previousX + x) / 2
                      : px(m.x);
                    const rightLimit = next
                      ? currentRight <= nextX
                        ? (currentRight + nextX) / 2
                        : (x + nextX) / 2
                      : px(m.x + m.width);
                    const targetWidth = Math.max(
                      touchChordTargets ? 44 : 2.1 * s,
                      symbolWidth + 0.44 * s,
                    );
                    const sidePadding = (targetWidth - symbolWidth) / 2;
                    const hitLeft = Math.max(leftLimit, x - sidePadding);
                    const hitRight = Math.min(rightLimit, currentRight + sidePadding);
                    const previousContentBottom =
                      si > 0
                        ? yTops[si - 1] +
                          staffHeight +
                          Math.max(sysStack[si - 1].block + voicingBlock, mainDrop[si - 1] * s) +
                          underlayHeights[si - 1] +
                          s
                        : 0;
                    const hitBottom = y + 0.6 * s;
                    // Use spare space above the symbol without covering low
                    // notes or other content from the preceding system.
                    const hitTop = Math.max(
                      previousContentBottom,
                      hitBottom - Math.max(touchChordTargets ? 44 : 0, 2.75 * s),
                    );
                    const activate = () => {
                      if (c.sourceId) onSelectChord?.(c.sourceId);
                    };
                    const onKeyDown = (event: KeyboardEvent<SVGGElement>) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      event.stopPropagation();
                      activate();
                    };
                    return (
                      <g
                        key={c.sourceId ?? ci}
                        data-chord-id={c.sourceId}
                        data-selected={selected ? 'true' : undefined}
                        role={interactive ? 'button' : undefined}
                        aria-label={interactive ? `${chordActionVerb} chord ${c.text}` : undefined}
                        aria-pressed={interactive ? selected : undefined}
                        tabIndex={interactive ? 0 : undefined}
                        style={{ cursor: interactive ? 'pointer' : undefined }}
                        onClick={
                          interactive
                            ? (event) => {
                                event.stopPropagation();
                                if (skipClickAfterDrag.current) {
                                  skipClickAfterDrag.current = false;
                                  return;
                                }
                                activate();
                              }
                            : undefined
                        }
                        onDoubleClick={
                          interactive && onChordDoubleClick
                            ? (event) => {
                                event.stopPropagation();
                                if (c.sourceId) onChordDoubleClick(c.sourceId);
                              }
                            : undefined
                        }
                        onKeyDown={interactive ? onKeyDown : undefined}
                      >
                        <rect
                          x={hitLeft}
                          y={hitTop}
                          width={Math.max(1, hitRight - hitLeft)}
                          height={Math.max(1, hitBottom - hitTop)}
                          rx={0.25 * s}
                          fill={selected ? 'var(--accent-soft, #e4efed)' : 'transparent'}
                          fillOpacity={selected ? 0.78 : 0}
                          stroke={selected ? 'var(--accent, #2f6f6a)' : 'none'}
                          strokeWidth={selected ? Math.max(1, 0.1 * s) : 0}
                          pointerEvents={interactive ? 'all' : 'none'}
                        />
                        <text
                          x={x}
                          y={y}
                          fontSize={2.1 * s}
                          fontWeight={600}
                          fill="currentColor"
                          fontFamily="var(--score-chord-font, var(--font-sans, sans-serif))"
                          >
                          {parts.prefix && <tspan data-chord-prefix>{parts.prefix}</tspan>}
                          <tspan data-chord-root>{parts.root}</tspan>
                          {parts.sup && (
                            <tspan fontSize={1.4 * s} dy={-0.75 * s}>
                              {parts.sup}
                            </tspan>
                          )}
                          {parts.bass && (
                            <tspan fontSize={2.0 * s} dy={parts.sup ? 0.75 * s : 0}>
                              {parts.bass}
                            </tspan>
                          )}
                          {parts.suffix && (
                            <tspan
                              data-chord-suffix
                              fontSize={2.1 * s}
                              dy={parts.sup && !parts.bass ? 0.75 * s : 0}
                            >
                              {parts.suffix}
                            </tspan>
                          )}
                        </text>
                      </g>
                    );
                  })}
                </g>
              ))}

              {/* Marks lane: boxed section letters + volta brackets, one
                  band above the chord row (headroom reserved via hasMarks) */}
              {hasMarks &&
                sys.measures.map((m) => {
                  const name = sectionStarts.get(m.barIndex);
                  if (!name) return null;
                  const laneC = y0 + marksC(si) * s;
                  const w = (1.1 + 0.95 * name.length) * s;
                  const left = px(m.x) - 0.3 * s;
                  return (
                    <g key={`sec-${m.barIndex}`} data-section-mark={name}>
                      <rect
                        x={left}
                        y={laneC - 1.1 * s}
                        width={w}
                        height={2.2 * s}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={0.14 * s}
                      />
                      <text
                        x={left + w / 2}
                        y={laneC}
                        fontSize={1.8 * s}
                        fontWeight={700}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fill="currentColor"
                        fontFamily="var(--font-sans, sans-serif)"
                      >
                        {name}
                      </text>
                    </g>
                  );
                })}
              {/* Navigation marks: segno/coda glyphs at the bar's start,
                  directives (D.S. al Coda, To Coda, break…) right-aligned
                  over the bar's END — the lead-sheet convention. */}
              {hasMarks &&
                sys.measures.map((m) => {
                  if (!m.directive && !m.feel && !m.segno && !m.coda) return null;
                  const laneC = y0 + marksC(si) * s;
                  // A glyph sharing its bar with a section letter (Misty's
                  // "Coda" box) steps right of the box instead of on it.
                  const sharedName = sectionStarts.get(m.barIndex);
                  const glyphNudge = sharedName ? (1.1 + 0.95 * sharedName.length + 0.5) * s : 0;
                  return (
                    <g key={`nav-${m.barIndex}`} data-nav-mark>
                      {(m.segno || m.coda) && (
                        <text
                          x={px(m.x) + 0.2 * s + glyphNudge}
                          y={laneC + 0.9 * s}
                          fontFamily="Bravura"
                          fontSize={2.6 * s}
                          fill="currentColor"
                        >
                          {m.segno ? glyphChar(SMUFL.segno) : glyphChar(SMUFL.coda)}
                        </text>
                      )}
                      {m.feel && (
                        // The authored Feel cue reads like the style direction
                        // it is: italic, at the bar's START, opposite the
                        // right-aligned navigation directives so the two can
                        // share a bar without colliding.
                        <text
                          x={px(m.x) + 0.2 * s + glyphNudge + (m.segno || m.coda ? 3 * s : 0)}
                          y={laneC}
                          fontSize={1.6 * s}
                          fontStyle="italic"
                          fontWeight={600}
                          dominantBaseline="central"
                          fill="currentColor"
                          fontFamily={TEXT_FONT}
                          data-feel-cue
                        >
                          {m.feel}
                        </text>
                      )}
                      {m.directive && (
                        <text
                          x={px(m.x + m.width) - 0.3 * s}
                          y={laneC}
                          fontSize={1.5 * s}
                          fontStyle="italic"
                          fontWeight={600}
                          textAnchor="end"
                          dominantBaseline="central"
                          fill="currentColor"
                          fontFamily={TEXT_FONT}
                        >
                          {m.directive}
                        </text>
                      )}
                    </g>
                  );
                })}
              {hasMarks &&
                voltaSpans.map((sp, k) => {
                  const first = sys.measures[0]?.barIndex ?? 0;
                  const last = sys.measures[sys.measures.length - 1]?.barIndex ?? -1;
                  if (sp.to < first || sp.from > last) return null;
                  const mFrom = sys.measures.find((m) => m.barIndex === Math.max(sp.from, first));
                  const mTo = sys.measures.find((m) => m.barIndex === Math.min(sp.to, last));
                  if (!mFrom || !mTo) return null;
                  const startsHere = sp.from >= first;
                  const endsHere = sp.to <= last;
                  const yL = y0 + voltaY(si) * s; // top lane: above the marks band
                  const x1 = px(mFrom.x) + (startsHere ? 0.1 : 0) * s;
                  const x2 = px(mTo.x + mTo.width) - 0.4 * s;
                  const hook = 1.7 * s;
                  const d = `${startsHere ? `M ${x1} ${yL + hook} L ${x1} ${yL} ` : `M ${x1} ${yL} `}L ${x2} ${yL}${
                    sp.closed && endsHere ? ` L ${x2} ${yL + hook}` : ''
                  }`;
                  return (
                    <g key={`volta-${k}`} data-volta={sp.n}>
                      <path d={d} fill="none" stroke="currentColor" strokeWidth={0.16 * s} />
                      {startsHere && sp.n > 0 && (
                        <text
                          x={x1 + 0.6 * s}
                          y={yL + 0.5 * s}
                          fontSize={1.5 * s}
                          dominantBaseline="hanging"
                          fill="currentColor"
                          fontFamily="Georgia, 'Times New Roman', serif"
                          fontStyle="italic"
                        >
                          {`${sp.n}.`}
                        </text>
                      )}
                    </g>
                  );
                })}

              {renderMeasures(sys.measures, px, py, interactiveIndex === 0, y0)}

              {hasExtras && (
                <g>
                  {/* System line joining the staff stack at the left edge */}
                  <line
                    x1={marginX}
                    x2={marginX}
                    y1={y0}
                    y2={y0 + stackTops[stackTops.length - 1] + staffHeight}
                    stroke="currentColor"
                    strokeWidth={ENGRAVING.staffLineThickness * s * 1.6}
                    pointerEvents="none"
                  />
                  {extraDrawn.map((drawnStaff, k) => {
                    const yk0 = y0 + stackTops[k];
                    const pyk = (yBelowTop: number) => yk0 + yBelowTop * s;
                    const kSys = drawnStaff.systems[si];
                    return (
                      <g key={k} data-bass-system={si} data-extra-staff={k}>
                        {renderStaffChrome(drawnStaff, kSys.width, contentX0, yk0, si)}
                        {renderMeasures(kSys.measures, px, pyk, interactiveIndex === k + 1, yk0)}
                      </g>
                    );
                  })}
                </g>
              )}

              {interactiveIndex !== null &&
                renderEntryCaret(
                  interactiveIndex === 0
                    ? sys.measures
                    : extraDrawn[interactiveIndex - 1].systems[si].measures,
                  px,
                  interactiveIndex === 0 ? y0 : y0 + stackTops[interactiveIndex - 1],
                  interactiveIndex,
                )}

              {(hasBracket || braces.length > 0 || (hasLabels && si === 0)) &&
                (() => {
                  const staffTops = [0, ...stackTops];
                  const topOf = (k: number) => y0 + staffTops[k];
                  const bottomOf = (k: number) => y0 + staffTops[k] + staffHeight;
                  return (
                    <g pointerEvents="none">
                      {hasBracket &&
                        (() => {
                          // Square ensemble bracket: a heavy bar left of the
                          // system line with outward-curving wings.
                          const yTop = topOf(0) - 0.2 * s;
                          const yBot = bottomOf(staffCount - 1) + 0.2 * s;
                          const bx = marginX - 1.1 * s;
                          const bw = 0.48 * s;
                          return (
                            <g data-score-bracket>
                              <rect
                                x={bx}
                                y={yTop}
                                width={bw}
                                height={yBot - yTop}
                                fill="currentColor"
                              />
                              <path
                                d={`M ${bx + bw} ${yTop} q ${0.9 * s} -0.05 ${1.15 * s} ${-0.85 * s}`}
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={0.16 * s}
                              />
                              <path
                                d={`M ${bx + bw} ${yBot} q ${0.9 * s} 0.05 ${1.15 * s} ${0.85 * s}`}
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={0.16 * s}
                              />
                            </g>
                          );
                        })()}
                      {braces.map((r, bi) => (
                        <path
                          key={`br-${bi}`}
                          data-group-brace
                          d={braceD(
                            marginX - (hasBracket ? 1.5 : 0.45) * s,
                            topOf(r.from),
                            bottomOf(r.to),
                            s,
                          )}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={0.18 * s}
                          strokeLinecap="round"
                        />
                      ))}
                      {si === 0 &&
                        labels.map((label, k) => {
                          if (!label) return null;
                          // A label on the first staff of a braced pair
                          // centres on the pair (the piano convention).
                          const run = braces.find((r) => r.from === k);
                          const yMid = run
                            ? (topOf(run.from) + bottomOf(run.to)) / 2
                            : (topOf(k) + bottomOf(k)) / 2;
                          return (
                            <text
                              key={`lb-${k}`}
                              data-staff-label
                              x={marginX - (hasBracket ? 2.8 : 1.6) * s}
                              y={yMid + 0.38 * s}
                              textAnchor="end"
                              fontSize={LABEL_FONT * s}
                              fontFamily={TEXT_FONT}
                              fill="currentColor"
                              opacity={0.85}
                            >
                              {label}
                            </text>
                          );
                        })}
                    </g>
                  );
                })()}

              {hasVoicing &&
                (() => {
                  const yv0 = y0 + staffHeight + sysStack[si].block + voicingGap;
                  const pyv = (yb: number) => yv0 + yb * s;
                  const inSystem = voicings!.filter((v) =>
                    sys.measures.some((m) => m.barIndex === v.barIndex),
                  );
                  const xForVoicing = (v: DrawnVoicing) => {
                    const m = sys.measures.find((mm) => mm.barIndex === v.barIndex)!;
                    const cs = m.chordSymbols.find((c) => c.beat === v.beat) ?? m.chordSymbols[0];
                    return px(m.x + (cs ? cs.x : 0));
                  };
                  const led = ledgerHalfWidth(SMUFL.noteheadWhole) * s;
                  return (
                    <g data-voicing-system={si} pointerEvents="none">
                      {Array.from({ length: staff.staffLines }, (_, li) => (
                        <line
                          key={li}
                          x1={marginX}
                          x2={contentX0 + sys.width * s}
                          y1={yv0 + li * s}
                          y2={yv0 + li * s}
                          stroke="currentColor"
                          strokeWidth={ENGRAVING.staffLineThickness * s}
                        />
                      ))}
                      <text
                        x={marginX + 0.2 * s}
                        y={pyv(3)}
                        data-clef="treble8vb"
                        fontFamily="Bravura"
                        fontSize={emSize}
                        fill="currentColor"
                      >
                        {g(SMUFL.gClef8vb)}
                      </text>
                      {inSystem.map((v, vi) => {
                        const vx = xForVoicing(v);
                        return (
                          <g key={vi} data-voicing={`${v.barIndex}:${v.beat}`}>
                            {v.notes.map((nn, ni) => {
                              const nx = vx + (nn.dx ?? 0) * s; // seconds step aside
                              return (
                                <g key={ni}>
                                  {nn.ledger.map((ly, k) => (
                                    <line
                                      key={k}
                                      data-ledger
                                      x1={nx - led}
                                      x2={nx + led}
                                      y1={pyv(ly)}
                                      y2={pyv(ly)}
                                      stroke="currentColor"
                                      strokeWidth={ENGRAVING.staffLineThickness * s}
                                    />
                                  ))}
                                  {nn.accidental !== undefined && (
                                    <text
                                      x={vx + (nn.accidentalDx ?? -1.0) * s}
                                      data-accidental={nn.accidental}
                                      y={pyv(nn.y)}
                                      fontFamily="Bravura"
                                      fontSize={emSize}
                                      textAnchor="middle"
                                      dominantBaseline="central"
                                      fill="currentColor"
                                    >
                                      {g(nn.accidental)}
                                    </text>
                                  )}
                                  <text
                                    x={nx}
                                    y={pyv(nn.y)}
                                    fontFamily="Bravura"
                                    fontSize={emSize}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fill="currentColor"
                                  >
                                    {g(SMUFL.noteheadWhole)}
                                  </text>
                                </g>
                              );
                            })}
                          </g>
                        );
                      })}
                    </g>
                  );
                })()}

              {hasTab &&
                (() => {
                  const yt0 = y0 + staffHeight + sysStack[si].block + voicingGap;
                  const pyt = (yb: number) => yt0 + yb * TAB_LINE_SPACING * s;
                  const inSystem = tab!.filter((t) =>
                    sys.measures.some((m) => m.barIndex === t.barIndex),
                  );
                  const xForChord = (t: TabChord) => {
                    const m = sys.measures.find((mm) => mm.barIndex === t.barIndex)!;
                    const cs = m.chordSymbols.find((c) => c.beat === t.beat) ?? m.chordSymbols[0];
                    return px(m.x + (cs ? cs.x : 0));
                  };
                  return (
                    <g data-tab-system={si} pointerEvents="none">
                      {Array.from({ length: TAB_STRINGS }, (_, li) => (
                        <line
                          key={li}
                          x1={marginX}
                          x2={contentX0 + sys.width * s}
                          y1={yt0 + li * TAB_LINE_SPACING * s}
                          y2={yt0 + li * TAB_LINE_SPACING * s}
                          stroke="currentColor"
                          strokeWidth={ENGRAVING.staffLineThickness * s}
                        />
                      ))}
                      {inSystem.map((t, ti) => {
                        const tx = xForChord(t);
                        return (
                          <g key={ti} data-tab={`${t.barIndex}:${t.beat}`}>
                            {t.stops.map((st, k) => {
                              const ty = pyt(tabStringY(st.stringIndex));
                              return (
                                <g key={k}>
                                  <rect
                                    x={tx - (st.fret >= 10 ? 0.85 : 0.55) * s}
                                    y={ty - 0.6 * s}
                                    width={(st.fret >= 10 ? 1.7 : 1.1) * s}
                                    height={1.2 * s}
                                    fill="var(--surface-2, #fff)"
                                  />
                                  <text
                                    x={tx}
                                    y={ty}
                                    fontFamily="var(--font-sans, sans-serif)"
                                    fontSize={1.15 * s}
                                    fontWeight={500}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fill="currentColor"
                                  >
                                    {st.fret}
                                  </text>
                                </g>
                              );
                            })}
                          </g>
                        );
                      })}
                    </g>
                  );
                })()}

              {cursorMeasureId &&
                (() => {
                  // The start cursor: where Play begins, the bar's opening
                  // barline drawn heavy and capped with a small triangle at
                  // the top line. It stays on the barline: inside the bar it
                  // would run through the first note, and above the staff
                  // through the bar number (whose ink ends 0.35 spaces up).
                  const m = sys.measures.find(
                    (mm) => measureIdByBarIndex.get(mm.barIndex) === cursorMeasureId,
                  );
                  if (!m) return null;
                  const x = px(m.x);
                  const top = y0 - 0.2 * s;
                  const bottom = y0 + staffHeight + sysStack[si].block + voicingBlock;
                  return (
                    <g data-start-cursor={cursorMeasureId} pointerEvents="none" aria-hidden="true">
                      <line
                        x1={x}
                        x2={x}
                        y1={top}
                        y2={bottom}
                        stroke="var(--accent, #2f6f6a)"
                        strokeWidth={Math.max(2, 0.28 * s)}
                      />
                      <path
                        d={`M ${x - 0.55 * s} ${top} L ${x + 0.55 * s} ${top} L ${x} ${top + 0.95 * s} Z`}
                        fill="var(--accent, #2f6f6a)"
                      />
                    </g>
                  );
                })()}
              {playheadBeat != null &&
                (() => {
                  const m = sys.measures.find((mm) => {
                    const start = barStartByIndex.get(mm.barIndex) ?? 0;
                    return playheadBeat >= start && playheadBeat < start + mm.beats;
                  });
                  if (!m) return null;
                  const start = barStartByIndex.get(m.barIndex) ?? 0;
                  // Track the drawn geometry (notes/chords), not a linear fraction.
                  const x = px(m.x + xAtBeatInMeasure(m, playheadBeat - start + 1));
                  const bottom = y0 + staffHeight + sysStack[si].block + voicingBlock;
                  return (
                    <line
                      x1={x}
                      x2={x}
                      y1={y0 - 0.5 * s}
                      y2={bottom}
                      stroke="var(--accent, #d4537e)"
                      strokeWidth={1.5}
                      opacity={0.85}
                      data-playhead
                    />
                  );
                })()}
            </g>
          );
        })}

        {renderTies(mainTies, (si) => yTops[si])}
        {renderSlurs(mainSlurCurves)}
        {renderLyrics(staff, (si) => yTops[si])}
        {renderHairpins(mainHairpins, (si) => yTops[si])}
        {hasExtras &&
          extraDrawn.map((_, k) => (
            <g key={`sp-${k}`}>
              {renderTies(extraTies[k], (si) => yTops[si] + sysStack[si].tops[k])}
              {renderSlurs(extraSlurCurves[k])}
              {renderHairpins(extraHairpins[k], (si) => yTops[si] + sysStack[si].tops[k])}
            </g>
          ))}
      </svg>
    </div>
  );
}
